import { A as AriaAttr, p as proxyCustomElement, H, c as createEvent } from './p-d7c3b756.js';
import { isEmpty, get, isEqual, map, includes, findIndex, filter, cloneDeep, find, flatten, debounce, isNil, head, difference } from 'lodash-es';
import { h } from './p-06a8959f.js';
import { B as BxfSelectConstants } from './p-2c52f7ab.js';
import { B as BxfFormValidationType } from './p-4003686b.js';
import BxfCheckboxMixedState2Obj from './p-6e64494a.js';
import BxfCheckmarkObj from './p-cdd1ffa8.js';
import { a as BxfTypeAheadUtil, b as getTextForResults } from './p-55739fe3.js';
import { e as BxfOverlayTriggerEnum, b as BxfDynamicPositionEnum } from './p-7397c455.js';
import { g as getParentOverlayContainer } from './p-89fcff47.js';
import { B as BxfOverlayDropdownController } from './p-dda8e7f3.js';
import { H as HyperList, f as flattenData, a as flattenDataWithFilter, B as BxfSelectInitialOption } from './p-bbf74898.js';
import { H as HasPopupValue } from './p-74342e6a.js';
import { O as WindowEventMap, P as eventPath, A as BXF_MOUSE_FOCUS, C as BXF_FOCUS_KEYBOARD_ONLY, z as BXF_ASSISTIVE_TEXT_HIDDEN, w as getKeyboardFocusCSS, u as toggleMouseFocus, q as getLabelProps, l as createSelector } from './p-0856cfca.js';
import { u as uuidv4 } from './p-2b83a1d1.js';
import { B as BxfPositionConstants } from './p-31d5cbf5.js';
import { F as FocusSource } from './p-462429f0.js';
import { K as KeyboardEventKey } from './p-b3de3bce.js';
import { R as Role } from './p-2c2985b9.js';
import { g as getAriaDescribedBy } from './p-1ed60a61.js';
import { F as FocusIndexManager } from './p-8b298ca7.js';
import { i as isRequiredValidator, d as defaultValidator, g as getValidator } from './p-97318b51.js';
import { B as BxfCheckboxConstants } from './p-e7df246f.js';
import { B as BxfTooltipConstants } from './p-08d21a8d.js';
import { B as BxfButtonSizeEnum } from './p-08621ebf.js';
import { B as BxfTextInputClear } from './p-65cb9fce.js';
import { B as BxfTextInputConstants } from './p-57102994.js';
import { L as LiveRegionPoliteness } from './p-aa4ef7c9.js';
import { B as BxfTypeAheadUiUtil } from './p-7b9aa141.js';
import { B as BxfDefaultState } from './p-7451d809.js';
import { i as isUsingI18n, B as BxfI18n, t as translateKeys } from './p-01256821.js';

class BxfSelectDropdownLoading {
    delegate;
    static containerWidth;
    constructor(delegate) {
        this.delegate = delegate;
    }
    static height() {
        return BxfSelectConstants.SIZES.LOADING_DROPDOWN_HEIGHT;
    }
    static isVisible(isLoading) {
        return isLoading;
    }
    isVisible() {
        return BxfSelectDropdownLoading.isVisible(this.delegate.isLoading);
    }
    render() {
        if (!this.isVisible())
            return;
        return (h("div", { style: {
                height: `${BxfSelectConstants.SIZES.LOADING_DROPDOWN_HEIGHT -
                    BxfSelectConstants.SIZES.PADDING_AND_MARGIN_SIZE}px`,
                width: `${BxfSelectDropdownLoading.containerWidth}px`,
            }, class: BxfSelectConstants.STRING.LOADING },
            h("bxf-progress-indicator", { loadingLabel: this.delegate.loadingLabel, type: 'loading', size: 'regular', isSpinner: true, hasLabel: true, dataTestSuffix: this.delegate.dataTestSuffix })));
    }
}

class BxfSelectErrorClassName {
    static getErrorClassName(isValid, validationType) {
        const { INPUT_CONTAINER_ERROR, INPUT_CONTAINER_WARNING } = BxfSelectConstants.STRING;
        if (!isValid) {
            return validationType === BxfFormValidationType.ERROR
                ? INPUT_CONTAINER_ERROR
                : INPUT_CONTAINER_WARNING;
        }
        return '';
    }
    static getSeparatorErrorClassName(isValid, validationType) {
        const { SEPARATOR_ERROR, SEPARATOR_WARNING } = BxfSelectConstants.STRING;
        if (!isValid) {
            return validationType === BxfFormValidationType.ERROR
                ? SEPARATOR_ERROR
                : SEPARATOR_WARNING;
        }
        return '';
    }
}

class BxfSelectInputWrapperClassName {
    static getInputWrapperClassName(isTimeScale, isLeftLabel) {
        return isTimeScale && isLeftLabel
            ? BxfSelectConstants.STRING.INPUT_WRAPPER_TIME_SCALE
            : BxfSelectConstants.STRING.INPUT_WRAPPER;
    }
}

const { OPTION_LABEL_BOLD, OPTION_SPAN } = BxfSelectConstants.STRING;
class BxfSelectOptionHighlight {
    static getOptionContentClasses(size, focusSourceCss = '') {
        return isEmpty(size) ? '' : `${OPTION_SPAN} ${focusSourceCss}`;
    }
    /**
     * Get highlighted span element for a character
     * 	- If the character is part of the match, it is bolded
     *  - If the character is not part of the match, it is not bold
     */
    static getDynamicHighlightedCharacter(option, character, characterIndex) {
        const spanEl = document.createElement('span');
        if (get(option, 'bxfMatch.dynamic', []).includes(characterIndex)) {
            spanEl.className = OPTION_LABEL_BOLD;
        }
        spanEl.innerText = character;
        return spanEl;
    }
    /**
     * Creates option content with dynamic highlighting for each character
     */
    static createDynamicOptionContent(option, size, focusSourceCss = '', hasTooltip = true) {
        const displayValue = get(option, 'displayValue', '');
        const parentEl = document.createElement('span');
        if (hasTooltip)
            parentEl.title = displayValue;
        parentEl.className = this.getOptionContentClasses(size, focusSourceCss);
        displayValue.split('').forEach((character, characterIndex) => {
            const spanEl = this.getDynamicHighlightedCharacter(option, character, characterIndex);
            spanEl && parentEl.appendChild(spanEl);
        });
        return parentEl;
    }
    /**
     * If the option has a match, create an array of children for the option with highlighting:
     * - Text before match
     * - Matched text
     */
    static createStaticOptionMatchChildren(option, displayValue) {
        const matchArr = [];
        if (option.bxfMatch && option.bxfMatch.startIndex >= 0) {
            const spanBeforeMatch = document.createElement('span');
            spanBeforeMatch.innerText = displayValue.substring(0, option.bxfMatch.startIndex);
            matchArr.push(spanBeforeMatch);
            if (option.bxfMatch.endIndex >= option.bxfMatch.startIndex) {
                const spanForMatch = document.createElement('span');
                spanForMatch.className = OPTION_LABEL_BOLD;
                spanForMatch.innerText = displayValue.substring(option.bxfMatch.startIndex, option.bxfMatch.endIndex);
                matchArr.push(spanForMatch);
            }
        }
        return matchArr;
    }
    /**
     * Creates array of children for option with non-dynamic option highlighting:
     * 	- Text before match
     * 	- Matched text
     * 	- Text after match
     */
    static createStaticOptionChildren(option, displayValue) {
        const childArr = this.createStaticOptionMatchChildren(option, displayValue);
        const spanAfterMatch = document.createElement('span');
        spanAfterMatch.innerText = displayValue.substring(option.bxfMatch?.endIndex || 0);
        return [...childArr, spanAfterMatch];
    }
    /**
     * Creates content for option with non-dynamic option highlighting
     */
    static createStaticOptionContent(option, size, hasTitle, focusSourceCss, hasTooltip) {
        const displayValue = option.displayValue || '';
        const parentEl = document.createElement('div');
        parentEl.className = this.getOptionContentClasses(size, focusSourceCss);
        if (hasTitle && hasTooltip) {
            parentEl.title = option.tooltip || displayValue;
        }
        const children = this.createStaticOptionChildren(option, displayValue);
        children?.forEach((child) => parentEl.appendChild(child));
        return parentEl;
    }
    /**
     * Creates content for option with highlighting based on the highlight mode.
     */
    static createOptionContent(option, size, highlightMode, hasTitle, focusSourceCss = '', hasTooltip = true) {
        return highlightMode === 'dynamic'
            ? this.createDynamicOptionContent(option, size, focusSourceCss, hasTooltip)
            : this.createStaticOptionContent(option, size, hasTitle, focusSourceCss, hasTooltip);
    }
}

class BxfSelectSimple {
    delegate;
    containerRef;
    dropdownRef;
    dropdownContainerRef;
    inputContainerRef;
    initialOptionRef;
    inputRef;
    overlay;
    hyperlist;
    selection;
    liveRegionText = '';
    _dataTest;
    _labelId = uuidv4();
    _valueId = uuidv4();
    _descriptionId = uuidv4();
    dummyCallFlag = false;
    isWindowsOS = false;
    constructor(delegate) {
        this.delegate = delegate;
        this.inputContainerOnClickHandler =
            this.inputContainerOnClickHandler.bind(this);
        this.dropdownContainerCloseCallback =
            this.dropdownContainerCloseCallback.bind(this);
        this.dropdownShouldMoveFocus = this.dropdownShouldMoveFocus.bind(this);
        this.optionOnClickHandler = this.optionOnClickHandler.bind(this);
        this.optionKeyDownHandler = this.optionKeyDownHandler.bind(this);
        this.parentRefHandler = this.parentRefHandler.bind(this);
        this.targetRefHandler = this.targetRefHandler.bind(this);
        this.sourceRefHandler = this.sourceRefHandler.bind(this);
        this.initialOptionRefHandler = this.initialOptionRefHandler.bind(this);
        this.initialOptionOnClickHandler =
            this.initialOptionOnClickHandler.bind(this);
        this.onResizeHandler = this.onResizeHandler.bind(this);
        this.dropdownRefHandler = this.dropdownRefHandler.bind(this);
        this.inputRefHandler = this.inputRefHandler.bind(this);
        this.disabledInteractionHandler =
            this.disabledInteractionHandler.bind(this);
        this.dropdownKeyDownHandler = this.dropdownKeyDownHandler.bind(this);
        this.mouseDownToggleListener = this.mouseDownToggleListener.bind(this);
        this.touchStartToggleListener =
            this.touchStartToggleListener.bind(this);
        this.tabKeyToggleListener = this.tabKeyToggleListener.bind(this);
        this.initialOptionKeyDownHandler =
            this.initialOptionKeyDownHandler.bind(this);
        this.updateState = this.updateState.bind(this);
        this.isOutsideOverlay = this.isOutsideOverlay.bind(this);
    }
    isLoadingChangedHandler() {
        if (this.dropdownContainerRef) {
            this.setDropdownContainerHeight();
            this.setDropdownWidth();
        }
        this.overlay?.updatePosition();
    }
    get optionHeight() {
        return this.delegate.isCompact
            ? BxfSelectConstants.SIZES.OPTION_HEIGHT_COMPACT
            : BxfSelectConstants.SIZES.OPTION_HEIGHT_REGULAR;
    }
    setValue(value) {
        if (!value) {
            this.initialOptionOnClickHandler(null);
            return;
        }
        this.clearSelections();
        const options = this.flattenOptions;
        if (typeof value === 'string') {
            const _selection = this.selectOption(value, options);
            if (!isEmpty(_selection)) {
                this.delegate.data = Object.assign([], this.delegate.data);
                this.delegate.inputValue = _selection.displayValue;
                this.delegate.setSelected(_selection);
            }
        }
        else if (value instanceof Array) {
            console.warn('Cannot set array for single select');
        }
        else if (typeof value === 'object') {
            const objectValue = value.bxfId ? value.bxfId : value.value;
            const _selection = this.selectOption(objectValue, options);
            if (!isEmpty(_selection) && !isEqual(_selection, value)) {
                this.delegate.data = Object.assign([], this.delegate.data);
                this.delegate.inputValue = _selection.displayValue;
                this.delegate.setSelected(_selection);
            }
        }
    }
    /**
     * Retrieves the BxfSelectOption corresponding to the given selection
     * which could represent EITHER the bxfId OR displayValue of a chosen option.
     * Since this value is ambiguous, we search the list of options by the bxfId
     * or displayValue
     */
    selectOption(selection, options) {
        let _selection;
        // first, search by display value...
        _selection = options.find((option) => option.displayValue === selection);
        // ...then, search by bxfId
        if (!_selection) {
            const bxfIdSelection = options.find((option) => option.bxfId === selection);
            const valueSelection = options.find((option) => option.value === selection);
            _selection = bxfIdSelection || valueSelection;
        }
        if (_selection) {
            _selection.isSelected = true;
            // Pass the value before the debounce/throttle time
            this.delegate.inputValueRaw = _selection.displayValue;
            this.delegate.delayedSearch(_selection.displayValue);
            this.dataDidUpdate();
        }
        return _selection;
    }
    /**
     * Returns the BxfSelectOption from the [data] that's associated
     * with the option selected from the dropdown
     */
    getSelectionObject(selection) {
        if (!selection)
            return null;
        const selectionId = get(selection, 'dataset.bxfId');
        return this.flattenOptions.find((option) => option.bxfId === selectionId);
    }
    initSelection() {
        const optionsSelected = this.flattenOptions.filter((item) => item.isSelected && !item.isDisabled);
        const option = this.flattenOptions.find((item) => item.isSelected && !item.isDisabled);
        if (option && optionsSelected.length === 1) {
            const optionChanged = this.delegate.inputValue !== option.displayValue;
            this.delegate.inputValue = option.displayValue;
            this.delegate.inputValueRaw = '';
            this.delegate.setSelected(option, optionChanged);
        }
        else if (this.delegate.inputValue !== this.delegate.selectNone) {
            this.delegate.inputValue = this.delegate.select;
        }
    }
    dataDidUpdate() {
        if (this.delegate.type !== 'filter-multiple') {
            this.initSelection();
        }
        if (this.overlay && this.overlay.isOpen()) {
            if (this.delegate.type === 'filter-multiple') {
                this.getMatches();
            }
            this.createDropdownContent();
        }
    }
    getMatches() {
        this.delegate.data.forEach((optionGroup) => optionGroup.values.forEach((option) => (option.bxfMatch = BxfTypeAheadUtil.findMatch(this.delegate.inputValueRaw, option.displayValue, this.delegate.highlightMode, option.value))));
        return this.flattenOptions;
    }
    get minWidth() {
        return undefined;
    }
    componentDidLoad() {
        this.initWindowResizer();
        this.initSelection();
        /* istanbul ignore next: Cypress tests in CI run in linux */
        if (navigator.userAgent.indexOf('Windows') !== -1) {
            this.isWindowsOS = true;
        }
    }
    componentDisconnected() {
        if (this.overlay) {
            this.overlay.close(this.isMouseFocus);
        }
    }
    initWindowResizer() {
        window.addEventListener(WindowEventMap.resize, this.onResizeHandler);
    }
    onResizeHandler() {
        this.setDropdownWidth();
    }
    openDropdown() {
        if (this.overlay) {
            this.overlay.open();
            return true;
        }
        return false;
    }
    closeDropdown() {
        if (this.overlay) {
            this.overlay.close(this.isMouseFocus);
            return true;
        }
        this.removeFocusStylingFromInputContainer();
        return false;
    }
    /**
     * Triggered whenever clear button focused/blurred
     * Refer to BxfTextInputClear for more details
     */
    clearDidChangeFocus(focus) {
        this.delegate.inputFocus = focus;
    }
    /**
     * Getter for keepMouseFocus value for BxfTextInputClearDelegate
     * @see BxfClearDelegateInterface
     */
    get keepMouseFocus() {
        return this.delegate.keepMouseFocus;
    }
    computeInitialOptionHeight() {
        let height = 0;
        const paddingTop = 4;
        const paddingBottom = 4;
        if (this.delegate.hasInitialOptionPlaceholder) {
            height = this.optionHeight + paddingTop + paddingBottom;
        }
        return height;
    }
    computeDropdownHeight(addLoadingHeight = true) {
        const itemHeights = this.optionHeight *
            this.flattenData.filter((elm) => elm !== BxfSelectConstants.STRING.OPTION_GROUP_BORDER).length;
        const paddingTop = 4;
        const paddingBottom = 4;
        const borderHeights = (this.delegate.data.length - 1) * (1 + paddingTop + paddingBottom);
        let height = itemHeights + 3 + borderHeights + paddingTop + paddingBottom;
        if (height > this.delegate.maxDropdownHeight) {
            height = this.delegate.maxDropdownHeight;
        }
        else {
            height += this.computeInitialOptionHeight(); // adding space for the initial option
        }
        if (BxfSelectDropdownLoading.isVisible(this.delegate.isLoading) &&
            addLoadingHeight) {
            height = BxfSelectDropdownLoading.height();
        }
        return height;
    }
    disabledInteractionHandler(event) {
        if (this.delegate.isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    }
    parentRefHandler(el) {
        if (el) {
            this.containerRef = el;
            this.containerRef.addEventListener(WindowEventMap.mousedown, this.disabledInteractionHandler, true);
        }
    }
    targetRefHandler(el) {
        if (el) {
            this.inputContainerRef = el;
            el.addEventListener(WindowEventMap.touchstart, this.touchStartToggleListener, false);
        }
    }
    sourceRefHandler(el) {
        if (el) {
            this.dropdownContainerRef = el;
        }
    }
    /**
     * Overridden in filter selectbox - aria-expanded not allowed on textbox
     */
    shouldToggleExpanded() {
        return true;
    }
    get overlayOptions() {
        return {
            trigger: BxfOverlayTriggerEnum.PRESSED,
            overlaysToClose: this.delegate?.shouldCloseOverlays
                ? [BxfPositionConstants.STRING.DROPDOWN_CONTAINER]
                : [],
            modifiers: {
                flipOrder: [
                    BxfDynamicPositionEnum.BOTTOM_LEFT,
                    BxfDynamicPositionEnum.TOP_LEFT,
                ],
                defaultPlacement: this.delegate?.overridePlacement ||
                    BxfDynamicPositionEnum.BOTTOM_LEFT,
            },
            targetElementOptions: {
                targetEventType: [WindowEventMap.mouseup],
                preventScroll: true,
            },
            shouldCloseCallback: this.dropdownContainerCloseCallback,
            arrowKeyCallback: (event) => this.dropdownDummyArrowCallback(event),
            keepMouseFocus: this.delegate?.keepMouseFocus,
            shouldToggleExpanded: this.shouldToggleExpanded(),
            skipFocusOnClose: true, // BxfOverlayDropdownController calls targetElement.focus
            shouldFocusCallback: this.dropdownShouldMoveFocus,
            shouldCloseOnMouseUp: true,
            useFocusVisible: true,
            parentContainer: getParentOverlayContainer(this.delegate?.parentOverlayContainerSelector),
        };
    }
    componentDidRender() {
        if (!this.overlay &&
            this.containerRef &&
            this.inputContainerRef &&
            this.dropdownContainerRef) {
            this.dummyCallFlag = false;
            this.overlay = new BxfOverlayDropdownController(this.delegate.host, this.containerRef, this.inputContainerRef, this.dropdownContainerRef, this.updateState, this.overlayOptions, this.isOutsideOverlay);
        }
    }
    /**
     * Custom outside overlay callback, close overlay when outside dropdown
     * 	(as opposed to generic overlay, which looks for any other overlay source)
     */
    isOutsideOverlay(event) {
        return !map(eventPath(event), BxfPositionConstants.STRING.CLASSNAME).find((el) => includes(el, BxfSelectConstants.STRING.DROPDOWN_CONTAINER));
    }
    getScrollDropdownHeight() {
        return (this.computeDropdownHeight(false) -
            this.computeInitialOptionHeight());
    }
    get selectedOptions() {
        return this.delegate.selected;
    }
    updateState(value) {
        if (value && this.overlay && !this.delegate.isReadOnly) {
            this.createDropdownContent();
            this.delegate.isOpen = true;
            this.overlay.openOverlayWithPosition();
            this.setDropdownContainerHeight();
            this.setDropdownHeight();
            this.setDropdownWidth();
            this.resetRovingTabIndex();
            this.scrollToFirstSelection();
        }
        else {
            // If flyout menus are open then we should not close the main menu overlay
            this.overlay = null;
            this.delegate.isOpen = false;
            if (this.hyperlist) {
                this.hyperlist.destroy();
                this.hyperlist = null;
            }
        }
    }
    /**
     * Manually set dropdown height
     */
    setDropdownHeight() {
        if (this.dropdownRef) {
            this.dropdownRef.style.height = `${this.getScrollDropdownHeight() / 16}rem`;
        }
    }
    /**
     * Manually set dropdown container height
     */
    setDropdownContainerHeight() {
        if (this.dropdownContainerRef) {
            this.dropdownContainerRef.style.height = `${this.computeDropdownHeight() / 16}rem`;
        }
    }
    // disable no-unused-vars - event used in types that extend simple
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialOptionOnClickHandler(_) {
        this.delegate.initialOptionValue = this.delegate.select;
        this.delegate.inputValue = this.delegate.select;
        this.clearSelections();
        this.closeDropdown();
        this.delegate.inputFocus = false;
        /**
         * Null is a valid value for the [selected] prop
         * which represents	the default [initialOptionPlaceholder]
         * option that has display value "Select none".
         * If [selected] = null, don't update [selected] again
         */
        if (this.delegate.selected != null) {
            this.delegate.setSelected(null, true, null, true);
        }
    }
    /**
     * Sets each bxf-select option's isSelected attribute to false
     * and removes any 'selected' styling
     */
    clearSelections() {
        this.delegate.data.forEach((optionGroup) => {
            optionGroup.values.forEach((option) => {
                option.isSelected = false;
            });
        });
        this.delegate.inputValueRaw = '';
        if (this.inputRef) {
            this.inputRef.value = '';
        }
        if (this.selection) {
            this.selection.classList.remove(BxfSelectConstants.STRING.OPTION_SELECTED);
            this.selection = undefined;
        }
    }
    initialOptionRefHandler(elm) {
        if (elm) {
            this.initialOptionRef = elm;
            elm.addEventListener(WindowEventMap.mousedown, () => {
                this.mouseDownToggleListener();
            });
            elm.addEventListener(WindowEventMap.keydown, this.initialOptionKeyDownHandler);
            elm.addEventListener(WindowEventMap.touchstart, this.touchStartToggleListener, { passive: true });
        }
    }
    createOptionGroupLabel(value) {
        const label = document.createElement('div');
        label.dataset.bxfNodeName = 'bxf-option-group-label';
        label.dataset.bxfSize = this.delegate.size;
        label.classList.add(BxfSelectConstants.STRING.OPTION_GROUP_LABEL);
        label.innerText = value;
        return label;
    }
    generateHyperListItem(options, index, filteredOptions) {
        const container = this.createOptionContainer(typeof options[index] !== 'string');
        if (options[index] === BxfSelectConstants.STRING.OPTION_GROUP_BORDER) {
            container.classList.add(BxfSelectConstants.STRING.OPTION_BORDER);
        }
        else if (typeof options[index] === 'string') {
            const label = this.createOptionGroupLabel(options[index]);
            container.appendChild(label);
        }
        else {
            const posIndex = findIndex(filteredOptions, (opt) => opt === options[index]);
            const option = this.createOption(options[index]);
            this.setAriaSetAttributes(option, posIndex);
            container.appendChild(option);
            if (options[index].isDisabled) {
                container.classList.add(BxfSelectConstants.STRING.OPTION_CONTAINER_DISABLED);
            }
        }
        return {
            element: container,
            height: options[index] === BxfSelectConstants.STRING.OPTION_GROUP_BORDER
                ? 9
                : this.optionHeight,
        };
    }
    /**
     * Get first focusable element in dropdown
     */
    getFirstFocusableElement() {
        return this.initialOptionRef &&
            this.delegate.hasInitialOptionPlaceholder
            ? this.initialOptionRef
            : this.getFocusableElementAtIndex(0);
    }
    /**
     * Reset roving tab index on rerender (make first option keyboard focusable)
     * If an option exists that is already focusable, do nothing.
     */
    resetRovingTabIndex() {
        if (!this.selection && !isEmpty(this.flattenOptions)) {
            const focusElem = this.getFirstFocusableElement();
            if (focusElem) {
                focusElem.tabIndex = 0;
            }
        }
    }
    createDropdownContent() {
        const flattenedData = this.flattenData;
        const filteredOptions = filter(flattenedData, (opt) => typeof opt !== 'string');
        if (this.hyperlist) {
            this.hyperlist.destroy();
        }
        if (this.dropdownRef) {
            const total = flattenedData.length;
            this.hyperlist = new HyperList(this.dropdownRef, {
                itemHeight: this.optionHeight,
                total: total >= 0 ? total : 0,
                rowClassName: BxfSelectConstants.STRING.OPTION_CONTAINER,
                isWindowsOS: this.isWindowsOS,
                generate: (index) => this.generateHyperListItem(flattenedData, index, filteredOptions),
            });
            // hide tr from screen readers
            const trElem = this.dropdownRef.querySelector('tr');
            trElem.setAttribute(AriaAttr.HIDDEN, 'true');
            this.resetRovingTabIndex();
        }
    }
    get flattenData() {
        return flattenData(this.delegate.data);
    }
    /**
     * List of the options that are selectable
     */
    get flattenOptions() {
        return this.flattenData.filter((option) => typeof option === 'object' && !option?.isDisabled);
    }
    scrollToFirstSelection() {
        if (this.hyperlist) {
            const index = this.flattenData.findIndex((option) => typeof option === 'object' && option.isSelected);
            this.hyperlist.scrollTo(index);
        }
    }
    /**
     * Query dropdown for an option element given an option object
     */
    getElemForOption(option) {
        return this.dropdownRef?.querySelector(`[data-bxf-id="${option.bxfId}"]`);
    }
    setDropdownWidth() {
        if (this.dropdownContainerRef) {
            BxfSelectDropdownLoading.containerWidth =
                this.inputContainerRef.offsetWidth -
                    BxfSelectConstants.SIZES.PADDING_AND_MARGIN_SIZE;
            this.dropdownContainerRef.style.width = this.delegate
                .maxDropdownWidth
                ? `${this.delegate.maxDropdownWidth}px`
                : `${this.inputContainerRef.offsetWidth}px`;
        }
    }
    /**
     * Re-focus input container after render
     */
    resetFocusToInputContainer() {
        window.setTimeout(() => {
            if (!this.inputRef?.classList.contains(BXF_MOUSE_FOCUS) &&
                !this.delegate.keepMouseFocus &&
                this.delegate._focusSource !== FocusSource.Keyboard) {
                this.inputContainerRef.classList.add(BXF_MOUSE_FOCUS);
            }
            this.inputContainerRef.focus();
        }, 50);
    }
    optionOnClickHandler(event) {
        const option = eventPath(event)?.find((elm) => elm.dataset && elm.dataset.bxfNodeName === 'bxf-option');
        this.clearSelections();
        this.selection = option;
        const selectionObject = this.getSelectionObject(this.selection);
        if (selectionObject) {
            selectionObject.isSelected = true;
            this.delegate.setSelected(selectionObject, true, event);
        }
        this.delegate.data = Object.assign([], this.delegate.data);
        this.closeDropdown();
        this.resetFocusToInputContainer();
    }
    /**
     * On Enter key, select option.
     */
    optionKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.Enter) {
            event.preventDefault();
            this.optionOnClickHandler(event);
        }
    }
    inputContainerOnClickHandler(event) {
        this.dummyCallFlag = false;
        event.preventDefault();
        event.stopPropagation();
        this.updateOverlay();
    }
    updateOverlay() {
        if (this.overlay && this.overlay.isOpen()) {
            this.setDropdownWidth();
            this.overlay.updatePosition();
        }
    }
    applyFocusStylingToInputContainer() {
        if (this.inputContainerRef) {
            if (!this.inputContainerRef.classList.contains(BxfSelectConstants.STRING.INPUT_CONTAINER_FOCUS)) {
                this.inputContainerRef.classList.add(BxfSelectConstants.STRING.INPUT_CONTAINER_FOCUS);
            }
            if (!this.inputContainerRef.classList.contains(BxfSelectConstants.STRING.INPUT_CONTAINER_KEY_FOCUS)) {
                this.inputContainerRef.classList.add(BxfSelectConstants.STRING.INPUT_CONTAINER_KEY_FOCUS);
            }
        }
    }
    removeFocusStylingFromInputContainer() {
        if (this.inputContainerRef &&
            this.inputContainerRef.classList.contains(BxfSelectConstants.STRING.INPUT_CONTAINER_FOCUS)) {
            this.inputContainerRef.classList.remove(BxfSelectConstants.STRING.INPUT_CONTAINER_FOCUS);
        }
    }
    createOptionContainer(isInteractive = true) {
        const optionContainer = document.createElement('li');
        optionContainer.dataset.bxfInteractive = String(isInteractive);
        optionContainer.setAttribute(AriaAttr.ROLE, Role.NONE);
        return optionContainer;
    }
    /**
     * Set appropriate ARIA roles and states for given option.
     */
    setSemanticsForOption(optionData, option) {
        option.setAttribute(AriaAttr.ROLE, Role.OPTION);
        if (optionData.isDisabled) {
            option.setAttribute(AriaAttr.DISABLED, 'true');
        }
        option.setAttribute(AriaAttr.SELECTED, `${optionData.isSelected || false}`);
        option.tabIndex = optionData.isSelected ? 0 : -1;
    }
    /**
     * Set aria-posinset and aria-setsize so that screen reader reads correct
     * position with lazy loading
     */
    setAriaSetAttributes(option, index) {
        const setSize = this.flattenOptions.length;
        const posInSet = index + 1;
        option.setAttribute(AriaAttr.SETSIZE, (this.delegate.hasInitialOptionPlaceholder
            ? setSize + 1
            : setSize).toString());
        option.setAttribute(AriaAttr.POSINSET, (this.delegate.hasInitialOptionPlaceholder
            ? posInSet + 1
            : posInSet).toString());
    }
    /**
     * Add event listeners to option
     */
    addEventListenersToOption(option) {
        option.addEventListener(WindowEventMap.keydown, this.optionKeyDownHandler);
        option.addEventListener(WindowEventMap.click, this.optionOnClickHandler);
    }
    /**
     * Dynamically create option span with display value
     * 	for options without highlighting
     */
    getDefaultOptionSpan(optionData) {
        const optionSpan = document.createElement('span');
        optionSpan.classList.add(BxfSelectConstants.STRING.OPTION_SPAN);
        const displayValue = optionData.displayValue || '';
        if (this.delegate.hasTooltip) {
            optionSpan.title = optionData.tooltip || displayValue;
        }
        optionSpan.innerText = displayValue;
        return optionSpan;
    }
    /**
     * Create option element with display value
     * NOTE: we need to create options parameter here
     * 	even though it only has the childEl property
     * 	so that multiple and filter-multiple can override
     * 	the options property with additional properties
     */
    createOption(optionData, options) {
        const option = document.createElement('div');
        this.setSemanticsForOption(optionData, option);
        if (!optionData.isDisabled) {
            this.addEventListenersToOption(option);
        }
        option.dataset.bxfNodeName = 'bxf-option';
        option.dataset.bxfDisplayValue = optionData.displayValue || '';
        option.dataset.bxfId = optionData.bxfId;
        option.classList.add(BxfSelectConstants.STRING.OPTION);
        option.classList.add(BxfSelectConstants.STRING.OPTION_SINGLE);
        if (this.delegate.isCompact) {
            option.classList.add(BxfSelectConstants.STRING.OPTION_COMPACT);
        }
        // show focus ring only on :focus-visible if keepMouseFocus=false
        if (!this.delegate.keepMouseFocus) {
            option.classList.add(BXF_FOCUS_KEYBOARD_ONLY);
        }
        if (optionData.isSelected && !optionData.isDisabled) {
            this.selection = option;
            option.classList.add(BxfSelectConstants.STRING.OPTION_SELECTED);
            this.delegate.inputValue = option.dataset.bxfDisplayValue;
            this.delegate.initialOptionValue = this.delegate.selectNone;
        }
        const childToAppend = options?.childEl ?? this.getDefaultOptionSpan(optionData);
        childToAppend && option.appendChild(childToAppend);
        return option;
    }
    get containerClasses() {
        const { CONTAINER, CONTAINER_LEFT_LABEL, CONTAINER_TIME_SCALE } = BxfSelectConstants.STRING;
        const isLeftLabel = this.delegate.isLeftLabel
            ? CONTAINER_LEFT_LABEL
            : '';
        const timescale = this.delegate.isTimeScale ? CONTAINER_TIME_SCALE : '';
        return `${CONTAINER} ${isLeftLabel} ${timescale}`;
    }
    get inputClasses() {
        const { INPUT, INPUT_SMALL, INPUT_LARGE, INPUT_SIMPLE_SELECT_DISABLED, } = BxfSelectConstants.STRING;
        const small = this.delegate && this.delegate.size === 'small' ? INPUT_SMALL : '';
        const large = this.delegate && this.delegate.size === 'large' ? INPUT_LARGE : '';
        const isSimpleAndMultipleSelectDisabled = [
            'simple',
            'multiple',
        ].includes(this.delegate?.type)
            ? INPUT_SIMPLE_SELECT_DISABLED
            : '';
        return `${INPUT} ${isSimpleAndMultipleSelectDisabled} ${small} ${large}`;
    }
    renderInput() {
        return (h("input", { tabIndex: -1, disabled: true, "aria-hidden": 'true', ref: this.inputRefHandler, placeholder: this.delegate.inputValue, class: this.inputClasses, title: this.delegate.hasTooltip ? this.delegate.inputValue : '' }));
    }
    /**
     * Assistive text for select value
     */
    getValueAssistiveText() {
        return this.delegate.inputValue;
    }
    /**
     * Render text visible to Screen Readers for select value
     */
    renderValueAssistiveText() {
        return (h("span", { id: this._valueId, class: BXF_ASSISTIVE_TEXT_HIDDEN }, this.getValueAssistiveText()));
    }
    inputRefHandler(elm) {
        if (elm) {
            this.inputRef = elm;
        }
    }
    iconClasses() {
        const { ICON, ICON_FOCUS, ICON_SMALL, ICON_LARGE } = BxfSelectConstants.STRING;
        const focus = this.delegate.isOpen ? ICON_FOCUS : '';
        const small = this.delegate.size === 'small' ? ICON_SMALL : '';
        const large = this.delegate.size === 'large' ? ICON_LARGE : '';
        return `${ICON} ${small} ${large} ${focus}`;
    }
    renderIcon() {
        return (h("bxf-icon", { class: this.iconClasses(), state: 'primary', type: 'data-select-caret-down', dataTestSuffix: this.delegate.dataTestSuffix }));
    }
    getInputContainerClasses() {
        const { INPUT_CONTAINER, INPUT_CONTAINER_SMALL, INPUT_CONTAINER_LARGE, INPUT_CONTAINER_DISABLED, INPUT_CONTAINER_READONLY, } = BxfSelectConstants.STRING;
        const disabled = this.delegate.isDisabled
            ? INPUT_CONTAINER_DISABLED
            : '';
        const readOnly = this.delegate.isReadOnly
            ? INPUT_CONTAINER_READONLY
            : '';
        let size = INPUT_CONTAINER;
        if (this.delegate.size === 'small') {
            size = INPUT_CONTAINER_SMALL;
        }
        else if (this.delegate.size === 'large') {
            size = INPUT_CONTAINER_LARGE;
        }
        const baseCss = `${size} ${BxfSelectErrorClassName.getErrorClassName(this.delegate.isValid, this.delegate.validationType)} ${disabled} ${readOnly}`;
        return getKeyboardFocusCSS(baseCss, this.delegate.keepMouseFocus);
    }
    renderInputContainerContent() {
        return [
            this.renderInput(),
            this.renderValueAssistiveText(),
            this.renderIcon(),
        ];
    }
    dropdownContainerCloseCallback() {
        if (this.delegate?._componentDidLoad && this.delegate?.validator) {
            this.delegate.validate();
        }
        return true;
    }
    /**
     * Move focus to dropdown if simple select clicked
     */
    dropdownShouldMoveFocus() {
        return true;
    }
    dropdownDummyArrowCallback(event) {
        switch (event.key) {
            case KeyboardEventKey.ArrowUp:
                if (!this.dummyCallFlag) {
                    this.navigateToIndex(this.flattenOptions.length - 1, this.flattenOptions.length === 0);
                }
                break;
            case KeyboardEventKey.ArrowDown:
                if (!this.dummyCallFlag) {
                    this.navigateToIndex(0, this.delegate.hasInitialOptionPlaceholder &&
                        this.initialOptionRef);
                }
                break;
            // no default
        }
    }
    /**
     * ID list to compute accessible name for single select
     */
    get inputAriaLabelledBy() {
        return `${this._labelId} ${this._valueId}`;
    }
    /**
     * Whether or not component focused via mouse
     * > Note: if keepMouseFocus enabled, this always returns false
     */
    get isMouseFocus() {
        return (this.delegate._focusSource === FocusSource.Mouse ||
            this.delegate._focusSource === FocusSource.Touch);
    }
    /**
     * Toggle focus source to given value.
     * If value is not keyboard, only change value if keepMouseFocus off
     */
    toggleFocusSource(focusSource) {
        if (focusSource === FocusSource.Keyboard ||
            !this.delegate.keepMouseFocus) {
            this.delegate._focusSource = focusSource;
        }
    }
    /**
     * Focus styling for components inside bxf-select
     */
    get focusSourceCss() {
        return `${this.isMouseFocus ? BXF_MOUSE_FOCUS : ''}`;
    }
    labelSizesCss() {
        const { LABEL_CONTAINER_LEFT_SMALL, LABEL_CONTAINER_LEFT_LARGE, LABEL_CONTAINER_LEFT_VALIDATION_SMALL, LABEL_CONTAINER_LEFT_VALIDATION_LARGE, REGULAR, SMALL, } = BxfSelectConstants.STRING;
        let isLeftLabelValMessage = '';
        let hasLeftLabelPadding = '';
        if (this.delegate.size !== REGULAR) {
            if (this.delegate.isLeftLabel) {
                hasLeftLabelPadding =
                    this.delegate.size === SMALL
                        ? LABEL_CONTAINER_LEFT_SMALL
                        : LABEL_CONTAINER_LEFT_LARGE;
            }
            if (!this.delegate.isValid && this.delegate.errorMessage) {
                isLeftLabelValMessage =
                    this.delegate.size === SMALL
                        ? LABEL_CONTAINER_LEFT_VALIDATION_SMALL
                        : LABEL_CONTAINER_LEFT_VALIDATION_LARGE;
            }
        }
        return {
            leftLabelPadding: hasLeftLabelPadding,
            validation: isLeftLabelValMessage,
        };
    }
    get labelWrapperCss() {
        const { LABEL_CONTAINER, LABEL_CONTAINER_COMPACT, LABEL_CONTAINER_TIME_SCALE, LABEL_CONTAINER_LEFT, LABEL_CONTAINER_LEFT_VALIDATION, LABEL_CONTAINER_TIME_SCALE_STACKED, REGULAR, } = BxfSelectConstants.STRING;
        const isLeftLabel = this.delegate.isLeftLabel
            ? LABEL_CONTAINER_LEFT
            : '';
        const isCompact = this.delegate.isCompact
            ? LABEL_CONTAINER_COMPACT
            : '';
        let isTimeScale = '';
        if (this.delegate.isTimeScale) {
            if (this.delegate.isStacked) {
                isTimeScale = LABEL_CONTAINER_TIME_SCALE_STACKED;
            }
            else {
                isTimeScale = LABEL_CONTAINER_TIME_SCALE;
            }
        }
        let isLeftLabelValMessage = '';
        let hasLeftLabelPadding = '';
        const { leftLabelPadding, validation } = this.labelSizesCss();
        hasLeftLabelPadding = leftLabelPadding;
        isLeftLabelValMessage = validation;
        if (this.delegate.isLeftLabel && this.delegate.size === REGULAR) {
            if (!this.delegate.isValid && this.delegate.errorMessage) {
                isLeftLabelValMessage = LABEL_CONTAINER_LEFT_VALIDATION;
            }
        }
        return `${LABEL_CONTAINER} ${isLeftLabel} ${isCompact} ${isTimeScale} ${hasLeftLabelPadding} ${isLeftLabelValMessage}`.trim();
    }
    get ariaHasPopup() {
        return this.delegate.isReadOnly
            ? HasPopupValue.FALSE
            : HasPopupValue.LISTBOX;
    }
    getAriaDescribedBy() {
        return getAriaDescribedBy(this.delegate.instructionalText, this._descriptionId);
    }
    renderInputContainer() {
        return (h("button", { ref: this.targetRefHandler, class: this.getInputContainerClasses(), onClick: this.inputContainerOnClickHandler, "aria-haspopup": this.ariaHasPopup, "aria-expanded": `${this.delegate.isOpen}`, "aria-labelledby": this.inputAriaLabelledBy, "aria-describedby": this.getAriaDescribedBy(), disabled: this.delegate.isDisabled || this.delegate.isReadOnly }, this.renderInputContainerContent()));
    }
    dropdownRefHandler(elm) {
        if (elm) {
            this.dropdownRef = elm;
            this.createDropdownContent();
            this.setDropdownContainerHeight();
            this.setDropdownHeight();
            this.setDropdownWidth();
            if (this.overlay?.isOpen) {
                this.overlay.moveFocusToOverlay();
            }
        }
    }
    /**
     * Wrapper around document.activeElement
     */
    getActiveElem() {
        return document.activeElement;
    }
    /**
     * Get focusable option in scroll area at given index
     */
    getFocusableElementAtIndex(index) {
        return isEmpty(this.flattenOptions)
            ? null
            : this.getElemForOption(this.flattenOptions[index]);
    }
    /**
     * Change keyboard focus to the option with a given index
     */
    changeFocusToIndex(index) {
        if (this.delegate.hasInitialOptionPlaceholder) {
            this.resetInitialTabIndex();
        }
        const newFocusElem = this.getFocusableElementAtIndex(index);
        if (newFocusElem) {
            toggleMouseFocus(newFocusElem, true); // NOTE: True means keyboard focus
            newFocusElem.tabIndex = 0;
            newFocusElem.focus();
        }
    }
    /**
     * Find index of focused option in flattenOptions
     */
    getActiveIndex(activeElem) {
        return this.flattenOptions.findIndex((option) => option.bxfId === activeElem.dataset.bxfId);
    }
    /**
     * Wait for next focusable element to render before focusing
     */
    changeFocusToIndexAfterRender(focusIndex) {
        window.setTimeout(() => {
            this.changeFocusToIndex(focusIndex);
        }, 50);
    }
    /**
     * Focus nth item in hyperlist.
     * @protected
     * @param {number} focusIndex
     */
    changeFocusToNthItem(focusIndex) {
        if (this.hyperlist) {
            this.hyperlist.scrollTo(focusIndex);
        }
        this.changeFocusToIndexAfterRender(focusIndex);
    }
    /**
     * Given keyboard event, active index, and focus index,
     *  determine whether or not to move to Select All
     * Move to select all/select none if:
     *  - home key pressed
     *  - down key pressed and last item active
     *  - up key pressed and first item active
     */
    shouldFocusInitialOption(event, activeIndex, focusIndex) {
        if (!this.initialOptionRef)
            return false;
        switch (event.key) {
            case KeyboardEventKey.Home:
                return true;
            case KeyboardEventKey.ArrowDown:
                return (activeIndex === this.flattenOptions.length - 1 &&
                    focusIndex === 0);
            case KeyboardEventKey.ArrowUp:
                return (activeIndex === 0 &&
                    focusIndex === this.flattenOptions.length - 1);
            default:
                return false;
        }
    }
    /**
     * Given an index, and whether this should be overridden,
     * 	navigate to either index or initial option
     */
    navigateToIndex(index, shouldFocusInitialOption) {
        if (this.delegate.hasInitialOptionPlaceholder &&
            shouldFocusInitialOption) {
            if (this.hyperlist) {
                this.hyperlist.scrollTo(0);
            }
            this.focusInitialOption();
        }
        else {
            this.dummyCallFlag = true;
            this.changeFocusToNthItem(index);
        }
    }
    /**
     * When Up/Down arrow keys pressed, move focus to previous/next option,
     * wrapping when first/last element reached.
     * When Home/End keys pressed, move focus to first/last element.
     * If initial option exists, account for initial option in select.
     * @param {KeyboardEvent} event
     */
    handleNavKeyEvent(event) {
        this.toggleFocusSource(FocusSource.Keyboard);
        const activeElem = this.getActiveElem();
        if (!isEmpty(this.flattenOptions) && activeElem) {
            event.preventDefault();
            event.stopPropagation();
            const activeIndex = this.getActiveIndex(activeElem);
            const focusIndex = FocusIndexManager.getFocusIndexForNavEvent(this.flattenOptions, activeIndex, event);
            activeElem.tabIndex = -1;
            const shouldNavigateToInitial = this.shouldFocusInitialOption(event, activeIndex, focusIndex);
            this.navigateToIndex(focusIndex, shouldNavigateToInitial);
        }
    }
    /**
     * Handle keydown events for dropdown
     */
    dropdownKeyDownHandler(event) {
        const navKeys = [
            ...FocusIndexManager.VERTICAL_NAV_KEYS,
            KeyboardEventKey.Home,
            KeyboardEventKey.End,
        ];
        if (navKeys.includes(event.key)) {
            this.handleNavKeyEvent(event);
        }
        else if (event.key === KeyboardEventKey.Tab) {
            this.toggleFocusSource(FocusSource.Keyboard);
        }
    }
    renderDropdown() {
        return (h("ul", { ref: this.dropdownRefHandler, class: BxfSelectConstants.STRING.DROPDOWN, role: Role.NONE, "data-bxf-is-loading": String(this.delegate.isLoading), "data-bxf-is-windows-os": String(this.isWindowsOS), onKeyDown: this.dropdownKeyDownHandler, onMouseDown: this.mouseDownToggleListener }));
    }
    renderDefaultAction() {
        return (this.delegate.hasInitialOptionPlaceholder &&
            !BxfSelectDropdownLoading.isVisible(this.delegate.isLoading) && (h("button", { role: Role.OPTION, "aria-posinset": '1', "aria-setsize": `${this.flattenOptions.length + 1}`, "aria-selected": `${!this.delegate.selected}`, ref: this.initialOptionRefHandler, onClick: this.initialOptionOnClickHandler, class: {
                [BxfSelectConstants.STRING.OPTION_CONTAINER_DEFAULT]: true,
                [BxfSelectConstants.STRING
                    .OPTION_CONTAINER_DEFAULT_COMPACT]: this.delegate.isCompact,
                [BxfSelectConstants.STRING
                    .OPTION_CONTAINER_DEFAULT_INITIAL]: true,
                [BXF_FOCUS_KEYBOARD_ONLY]: !this.delegate.keepMouseFocus,
            } },
            h("div", { class: {
                    [BXF_FOCUS_KEYBOARD_ONLY]: !this.delegate.keepMouseFocus,
                    [BxfSelectConstants.STRING.OPTION]: true,
                    [BxfSelectConstants.STRING.OPTION_COMPACT]: this.delegate.isCompact,
                } },
                h("span", { class: {
                        [BxfSelectConstants.STRING.OPTION_SPAN]: true,
                        [BXF_FOCUS_KEYBOARD_ONLY]: !this.delegate.keepMouseFocus,
                    }, title: this.delegate.hasTooltip
                        ? this.delegate.initialOptionValue
                        : '' }, this.delegate.initialOptionValue)))));
    }
    renderActionButton() {
        return undefined;
    }
    renderLoading() {
        return new BxfSelectDropdownLoading(this.delegate).render();
    }
    /**
     * Disable focus ring on mouse click
     */
    mouseDownToggleListener() {
        this.toggleFocusSource(FocusSource.Mouse);
        this.delegate.inputKeyFocus = false;
    }
    /**
     * Disable focus ring on touch
     */
    /* istanbul ignore next: touchstart fires mousedown, see ADO-1255700 */
    touchStartToggleListener() {
        this.toggleFocusSource(FocusSource.Touch);
    }
    /**
     * Re-enable focus ring on tab key press
     */
    tabKeyToggleListener(event) {
        if (event.key === KeyboardEventKey.Tab) {
            if (this.inputContainerRef &&
                this.inputContainerRef.classList.contains(BXF_MOUSE_FOCUS)) {
                this.inputContainerRef.classList.remove(BXF_MOUSE_FOCUS);
            }
            this.toggleFocusSource(FocusSource.Keyboard);
        }
    }
    /**
     * When initial option navigated away from, reset tabindex to -1 to
     * preserve roving tabindex.
     * @protected
     */
    resetInitialTabIndex() {
        if (this.initialOptionRef) {
            this.initialOptionRef.tabIndex = -1;
        }
    }
    /**
     * Handle keydown for initial option
     * Tab key - turn on focus outline
     * Down arrow - go to first option
     * Up arrow - go to last option
     * @protected
     * @param {KeyboardEvent} event
     */
    initialOptionKeyDownHandler(event) {
        switch (event.key) {
            case KeyboardEventKey.ArrowDown:
                event.stopPropagation();
                this.changeFocusToNthItem(0);
                break;
            case KeyboardEventKey.End:
            case KeyboardEventKey.ArrowUp:
                event.stopPropagation();
                this.changeFocusToNthItem(this.flattenOptions.length - 1);
                break;
        }
    }
    renderListbox() {
        return (h("div", { role: Role.LISTBOX, class: BxfSelectConstants.STRING.DROPDOWN_LISTBOX, "aria-required": `${this.isSelectionRequired()}`, "aria-invalid": `${!this.delegate.isValid}`, "aria-label": this.delegate.baseLabel, "aria-description": this.delegate.instructionalText ?? null, "aria-busy": this.delegate.isLoading ? 'true' : null },
            this.renderDefaultAction(),
            this.renderDropdown()));
    }
    renderDropdownContainer() {
        return (h("div", { ref: this.sourceRefHandler, onMouseDown: (event) => event.stopPropagation(), class: BxfSelectConstants.STRING.DROPDOWN_CONTAINER }, [
            this.renderLoading(),
            this.renderListbox(),
            this.renderActionButton(),
        ]));
    }
    isSelectionRequired() {
        return (this.delegate.isRequired ||
            isRequiredValidator(this.delegate.validator));
    }
    renderAsterisk() {
        return (this.isSelectionRequired() && (h("div", { class: BxfSelectConstants.STRING.LABEL_ASTERISK, "aria-hidden": 'true' }, "*")));
    }
    get labelClasses() {
        const { LABEL, LABEL_COMPACT, LABEL_DISABLED, LABEL_WITH_INSTRUCTIONAL_TEXT, } = BxfSelectConstants.STRING;
        const disabled = this.delegate.isDisabled ? LABEL_DISABLED : '';
        const labelWithInstructionalText = this.delegate.instructionalText
            ? LABEL_WITH_INSTRUCTIONAL_TEXT
            : '';
        const isCompact = this.delegate.isCompact ? LABEL_COMPACT : '';
        return `${LABEL} ${isCompact} ${disabled} ${labelWithInstructionalText}`;
    }
    /**
     * Render assistive text visible to Screen Readers for all select types EXCEPT
     * 	filter. Filter does not require labelledby as
     * 	textbox only labeled by label/assistiveText prop
     */
    renderAssistiveText() {
        return (h("span", { id: this._labelId, class: BXF_ASSISTIVE_TEXT_HIDDEN }, this.delegate.assistiveText));
    }
    /**
     * Private function that handles label for text input container
     */
    renderLabelContainer() {
        if (this.delegate.label || this.delegate.isLeftLabel) {
            return (h("div", { class: this.labelWrapperCss },
                h("div", { id: this._labelId, class: this.labelClasses }, this.delegate.label),
                this.renderAsterisk()));
        }
        return this.renderAssistiveText();
    }
    renderErrorMessage() {
        if (!this.delegate.isValid && !isEmpty(this.delegate.errorMessage)) {
            return (h("span", { class: this.delegate.validationType ===
                    BxfFormValidationType.ERROR
                    ? BxfSelectConstants.STRING.ERROR_MESSAGE
                    : BxfSelectConstants.STRING.WARNING_MESSAGE }, this.delegate.errorMessage));
        }
        return h("span", null);
    }
    /**
     * Wrap error message in live region so that screen reader will announce any errors
     * NOTE - no ID - listbox is outside shadow dom so aria-describedby will ref invalid id
     */
    renderErrorMessageWrapper() {
        return (h("span", { role: Role.ALERT, "aria-atomic": 'true' }, this.renderErrorMessage()));
    }
    /**
     * Re-focus input after combobox component re-rendered
     */
    resetFocusToInput() {
        window.setTimeout(() => {
            this.inputRef?.focus();
        }, 50);
    }
    /**
     * Reset focus to initial option after render
     */
    focusInitialOption() {
        window.setTimeout(() => {
            toggleMouseFocus(this.initialOptionRef, true); // NOTE: True means keyboard focus
            this.initialOptionRef.focus();
        }, 50);
    }
    /**
     * Update text of live region with number of matches found
     */
    updateLiveRegionText() {
        this.liveRegionText = getTextForResults(this.flattenOptions, this.delegate.inputValueRaw || '');
    }
    /**
     * For comboboxes, alert screen reader users when results found
     * For simple list boxes, this is blank
     */
    renderLiveRegion() {
        return h("span", { class: BXF_ASSISTIVE_TEXT_HIDDEN });
    }
    /**
     * Render wrapper for input and dropdown
     */
    renderInputWrapper() {
        return (h("div", { class: BxfSelectInputWrapperClassName.getInputWrapperClassName(this.delegate.isTimeScale, this.delegate.isLeftLabel) },
            this.renderInstructionalText(),
            this.renderInputContainer(),
            this.renderErrorMessageWrapper(),
            this.renderDropdownContainer(),
            this.renderLiveRegion()));
    }
    renderInstructionalText() {
        const { INSTRUCTIONAL_TEXT } = BxfSelectConstants.STRING;
        return (this.delegate.instructionalText && (h("div", { class: INSTRUCTIONAL_TEXT },
            h("span", { id: this._descriptionId }, this.delegate.instructionalText))));
    }
    render(dataTest) {
        if (dataTest && !this._dataTest) {
            this._dataTest = dataTest;
        }
        return (h("div", { style: { minWidth: this.minWidth }, ref: this.parentRefHandler, class: this.containerClasses, "data-test": this._dataTest },
            this.renderLabelContainer(),
            this.renderInputWrapper()));
    }
}

const { CONTENT } = BxfTooltipConstants.STRING;
const { TOOLTIP_CONTENT_MAX } = BxfSelectConstants.SIZES;
class BxfSelectMultiple extends BxfSelectSimple {
    delegate;
    oldData = [];
    shouldApply = false;
    actionButtonRef;
    constructor(delegate) {
        super(delegate);
        this.delegate = delegate;
        this.onPillClickClearHandler = this.onPillClickClearHandler.bind(this);
        this.onPillMouseDownHandler = this.onPillMouseDownHandler.bind(this);
        this.actionButtonOnClickHandler =
            this.actionButtonOnClickHandler.bind(this);
        this.actionButtonRefHandler = this.actionButtonRefHandler.bind(this);
        this.checkboxKeyDownListener = this.checkboxKeyDownListener.bind(this);
        this.disabledCheckboxKeyDownHandler =
            this.disabledCheckboxKeyDownHandler.bind(this);
    }
    dataDidUpdate() {
        super.dataDidUpdate();
        this.delegate.setSelected(this.selectedOptions, false);
        this.setInitialOptionState();
    }
    componentDidLoad() {
        super.componentDidLoad();
        if (this.dropdownRef && this.dropdownRef.parentElement) {
            this.dropdownRef.parentElement.setAttribute(AriaAttr.MULTISELECTABLE, 'true');
        }
        this.setInitialOptionState();
    }
    actionButtonRefHandler(elm) {
        this.actionButtonRef = elm;
    }
    isDropdownOpenedDidChange() {
        if (!this.delegate.applyButton)
            return;
        if (this.delegate.isOpen) {
            this.oldData = cloneDeep(this.delegate.data || []);
        }
        else if (!this.shouldApply) {
            this.delegate.data = cloneDeep(this.oldData);
            this.oldData = [];
        }
        this.shouldApply = false;
        this.delegate.applyButtonIsDisabled = true;
    }
    actionButtonOnClickHandler() {
        this.shouldApply = true;
        this.delegate.applyButtonClicked.emit({
            srcEvent: null,
            oldData: this.oldData,
            newData: this.delegate.data,
        });
        this.closeDropdown();
    }
    setValue(value) {
        if (isEqual(this.selectedOptions, value))
            return;
        this.selectAll(false);
        if (isEmpty(value)) {
            this.delegate.setSelected([]);
            return;
        }
        const options = this.flattenOptions;
        if (typeof value === 'string') {
            this.selectOption(value, options);
        }
        else if (value instanceof Array) {
            value.forEach((option) => this.selectOption(option.displayValue, options));
        }
        else if (typeof value === 'object') {
            this.selectOption(value.displayValue, options);
        }
        this.setInitialOptionState();
        this.delegate.data = Object.assign([], this.delegate.data);
        this.createDropdownContent();
        this.delegate.setSelected(this.selectedOptions);
    }
    get minWidth() {
        return `${133 / 16}rem`;
    }
    get initialOptionLabelClasses() {
        return this.delegate.initialOptionState === 'initial'
            ? BxfSelectConstants.STRING.OPTION_LABEL_BOLD
            : '';
    }
    /**
     * Set tabindex to -1 on closing so that roving tabindex is limited to 1 element.
     */
    handleCheckboxEscKey(event) {
        const path = eventPath(event);
        const option = find(path, (elm) => elm.classList &&
            elm.classList.contains(BxfSelectConstants.STRING.OPTION_CHECKBOX));
        if (option) {
            option.tabIndex = -1;
        }
    }
    /**
     * Check/uncheck checkbox on spacebar press
     */
    checkboxSpaceHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        const path = eventPath(event);
        const defaultOption = find(path, (elm) => elm.classList &&
            elm.classList.contains(BxfSelectConstants.STRING.OPTION_CONTAINER_DEFAULT));
        if (defaultOption) {
            this.initialOptionOnClickHandler();
        }
        else {
            this.optionOnClickHandler(event);
        }
    }
    /**
     * Handles keydown events:
     * - Tab, navigation keys - restore focus ring
     * - Esc: reset tabindex and close
     */
    checkboxKeyDownListener(event) {
        switch (event.key) {
            case KeyboardEventKey.SpaceBar:
                this.checkboxSpaceHandler(event);
                break;
            case KeyboardEventKey.Escape:
                this.handleCheckboxEscKey(event);
                break;
        }
    }
    renderDefaultAction() {
        if (this.delegate.hasInitialOptionPlaceholder &&
            !BxfSelectDropdownLoading.isVisible(this.delegate.isLoading)) {
            const checkbox = this.createCheckbox({
                displayValue: this.delegate.selectAll,
                isSelected: this.delegate.initialOptionState === 'checked' ||
                    this.delegate.initialOptionState === 'mixed',
            }, {
                isMixed: this.delegate.initialOptionState === 'mixed',
                labelClasses: this.initialOptionLabelClasses,
            });
            return (h("div", { ref: this.initialOptionRefHandler, class: {
                    [BxfSelectConstants.STRING.OPTION_CONTAINER_DEFAULT]: true,
                    [BxfSelectConstants.STRING
                        .OPTION_CONTAINER_DEFAULT_COMPACT]: this.delegate.isCompact,
                    [BxfSelectConstants.STRING
                        .OPTION_CONTAINER_DEFAULT_CHECKBOX]: true,
                    [BXF_FOCUS_KEYBOARD_ONLY]: !this.delegate.keepMouseFocus,
                }, "data-bxf-node-name": 'bxf-option', role: Role.OPTION, "aria-posinset": '1', "aria-setsize": `${this.flattenOptions.length + 1}`, "aria-selected": `${this.delegate.initialOptionState === 'checked'}`, onKeyDown: this.checkboxKeyDownListener, onClick: this.initialOptionOnClickHandler, tabIndex: 0 },
                h("div", { class: {
                        [BxfSelectConstants.STRING.OPTION]: true,
                        [BxfSelectConstants.STRING.OPTION_COMPACT]: this.delegate.isCompact,
                        [BxfSelectConstants.STRING.OPTION_CHECKBOX]: true,
                        [BxfSelectConstants.STRING.OPTION_CHECKBOX_LARGE]: this.delegate.size === 'large',
                        [BXF_FOCUS_KEYBOARD_ONLY]: !this.delegate.keepMouseFocus,
                    }, ref: (elm) => {
                        if (elm) {
                            if (!isEmpty(elm.children)) {
                                elm.children[0].remove();
                            }
                            elm.appendChild(checkbox);
                        }
                    }, title: this.delegate.hasTooltip
                        ? this.delegate.selectAll
                        : '' })));
        }
        return null;
    }
    areAllOptionsSelected() {
        return this.selectedOptions.length === this.flattenOptions.length;
    }
    initialOptionOnClickHandler(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (this.areAllOptionsSelected()) {
            this.selectAll(false);
        }
        else {
            this.selectAll(true);
        }
        this.delegate.applyButtonIsDisabled = false;
        this.delegate.setSelected(this.selectedOptions, true, event);
        // do not move focus if option clicked using mouse
        if (!event || this.delegate.keepMouseFocus) {
            this.focusInitialOption();
        }
    }
    /**
     * Handle the following keyboard events for option:
     *  - Tab, arrows: re-enable focus ring
     *  - Escape: reset tabindex when closing
     * Note: this overrides keyboard handler in bxf-select-simple, which has
     * 	selection event on Enter key.
     * @protected
     * @param {KeyboardEvent} event
     */
    optionKeyDownHandler(event) {
        this.checkboxKeyDownListener(event);
    }
    /**
     * Sets the isSelected property of each option to the
     * value of @param checked
     */
    selectAll(checked) {
        this.delegate.data.forEach((optionGroup) => optionGroup.values.forEach((option) => {
            if (!option.isDisabled) {
                option.isSelected = checked;
            }
        }));
        this.setInitialOptionState();
        this.delegate.data = Object.assign([], this.delegate.data);
        this.createDropdownContent();
        if (!checked) {
            this.resetRovingTabIndex();
        }
    }
    computeDropdownHeight() {
        let height = super.computeDropdownHeight();
        if (this.delegate.applyButton) {
            height += get(this.actionButtonRef, 'offsetHeight', 40);
        }
        if (height > this.delegate.maxDropdownHeight) {
            height = this.delegate.maxDropdownHeight;
        }
        if (BxfSelectDropdownLoading.isVisible(this.delegate.isLoading)) {
            height = BxfSelectDropdownLoading.height();
        }
        return height;
    }
    /**
     * Set all aria attributes for given option.
     * Since this is a multiselect we also need to set aria-selected
     * 	on all unchecked options to false
     */
    setSemanticsForOption(optionData, option) {
        super.setSemanticsForOption(optionData, option);
        if (!optionData.isSelected) {
            option.setAttribute(AriaAttr.SELECTED, 'false');
        }
        option.tabIndex = -1;
    }
    /**
     * Prevent scrolling when user attempts to toggle/untoggle disabled checkbox
     */
    disabledCheckboxKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.SpaceBar) {
            event.preventDefault();
        }
    }
    createOption(optionData, options) {
        const option = document.createElement('div');
        if (optionData.isDisabled) {
            option.addEventListener(WindowEventMap.keydown, this.disabledCheckboxKeyDownHandler);
        }
        else {
            this.addEventListenersToOption(option);
        }
        option.classList.add(BxfSelectConstants.STRING.OPTION);
        option.classList.add(BxfSelectConstants.STRING.OPTION_CHECKBOX);
        if (this.delegate.isCompact) {
            option.classList.add(BxfSelectConstants.STRING.OPTION_COMPACT);
        }
        // show focus ring only on :focus-visible if keepMouseFocus=false
        if (!this.delegate.keepMouseFocus) {
            option.classList.add(BXF_FOCUS_KEYBOARD_ONLY);
        }
        if (this.delegate.size === 'large') {
            option.classList.add(BxfSelectConstants.STRING.OPTION_CHECKBOX_LARGE);
        }
        option.dataset.bxfNodeName = 'bxf-option';
        option.dataset.bxfId = optionData.bxfId;
        option.dataset.bxfDisplayValue = optionData.displayValue || '';
        this.setSemanticsForOption(optionData, option);
        option.appendChild(this.createCheckbox(optionData, options));
        return option;
    }
    /**
     * Create checkbox element
     */
    createCheckbox(optionData, options) {
        const checkboxContainer = document.createElement('div');
        checkboxContainer.classList.add(BxfCheckboxConstants.STRING.CONTAINER);
        if (this.delegate.size === 'large') {
            checkboxContainer.classList.add(BxfCheckboxConstants.STRING.CONTAINER_LARGE);
        }
        if (optionData.isDisabled) {
            checkboxContainer.classList.add(BxfCheckboxConstants.STRING.DISABLED);
        }
        const checkbox = document.createElement('div');
        checkbox.dataset.defaultChecked = `${optionData.isSelected || false}`;
        checkbox.classList.add(BxfSelectConstants.STRING.PSEUDO_CHECKBOX);
        // checkbox decorative, focus is on option
        checkbox.setAttribute(AriaAttr.HIDDEN, 'true');
        checkbox.dataset.bxfNodeName = 'bxf-checkbox-input';
        if (optionData.isDisabled) {
            checkbox.dataset.disabled = 'disabled';
        }
        const icon = document.createElement('div');
        icon.dataset.bxfNodeName = 'bxf-checkbox-icon';
        icon.classList.add(BxfCheckboxConstants.STRING.CHECKBOX);
        icon.classList.add(BxfCheckboxConstants.STRING.CHECKBOX_REGULAR);
        if (options && options.isMixed) {
            icon.classList.add(`bxf-select__checkbox-mixed-state-2--checkbox`);
            icon.innerHTML = BxfCheckboxMixedState2Obj.svg;
        }
        else if (optionData.isSelected) {
            icon.classList.add(`bxf-select__checkmark--checkbox`);
            icon.innerHTML = BxfCheckmarkObj.svg;
        }
        const label = document.createElement('div');
        label.classList.add(BxfCheckboxConstants.STRING.LABEL);
        if (options && options.labelClasses) {
            label.classList.add(options.labelClasses);
        }
        if (options && options.childEl) {
            label.appendChild(options.childEl);
        }
        else {
            label.innerText = optionData.displayValue || '';
        }
        const labelContainer = document.createElement('label');
        labelContainer.classList.add(BxfCheckboxConstants.STRING.LABEL_CONTAINER);
        labelContainer.appendChild(checkbox);
        labelContainer.appendChild(icon);
        labelContainer.appendChild(label);
        checkboxContainer.appendChild(labelContainer);
        checkboxContainer.title = this.delegate.hasTooltip
            ? optionData.tooltip || optionData.displayValue
            : '';
        return checkboxContainer;
    }
    /**
     * Change focus back to given option after re-render
     */
    resetFocusForOption(option) {
        window.setTimeout(() => {
            const newCheckbox = this.dropdownRef.querySelector(`[data-bxf-id="${option.dataset.bxfId}"]`);
            // Adding a null check
            if (newCheckbox) {
                // turn focus ring on if mouse focus, off if keyboard focus
                toggleMouseFocus(newCheckbox, !this.isMouseFocus);
                newCheckbox.focus();
            }
        }, 50);
    }
    optionOnClickHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        const path = eventPath(event);
        const option = path.find((elm) => get(elm, 'dataset.bxfNodeName') === 'bxf-option');
        const checkbox = option.querySelector("[data-bxf-node-name='bxf-checkbox-input']");
        const newSelectedVal = get(checkbox, 'dataset.defaultChecked', 'false') === 'false';
        if (path[0].nodeName !== 'INPUT') {
            checkbox.dataset.defaultChecked = `${newSelectedVal}`;
        }
        this.updateDataState(option.dataset.bxfId, newSelectedVal);
        this.delegate.setSelected(this.selectedOptions, true, event);
        this.delegate.applyButtonIsDisabled = false;
        this.resetFocusForOption(option);
    }
    /**
     * Returns a list of options where isSelected = true
     */
    get selectedOptions() {
        return flatten(this.delegate.data.map((optionGroup) => optionGroup.values)).filter((option) => option.isSelected);
    }
    /**
     * Sets the state of the checkbox in the initial option
     * based on the number of options selected
     */
    setInitialOptionState() {
        if (this.areAllOptionsSelected()) {
            this.delegate.initialOptionState = 'checked';
        }
        else if (this.selectedOptions.length > 0) {
            this.delegate.initialOptionState = 'mixed';
        }
        else {
            this.delegate.initialOptionState = 'initial';
        }
    }
    updateDataState(bxfId, isSelected) {
        this.delegate.data.forEach((optionGroup) => {
            const option = optionGroup.values.find((opt) => opt.bxfId === bxfId);
            if (option) {
                option.isSelected = isSelected;
            }
        });
        this.setInitialOptionState();
        this.delegate.data = Object.assign([], this.delegate.data);
    }
    onPillMouseDownHandler(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    onPillClickClearHandler(event) {
        this.selectAll(false);
        this.delegate.setSelected(this.selectedOptions, true, event);
    }
    get pillSize() {
        if (this.delegate.size === 'small') {
            return 'xsmall';
        }
        if (this.delegate.size === 'regular') {
            return 'small';
        }
        return 'large';
    }
    /**
     * Find index of focused option in flattenOptions
     * @protected
     * @param {*} activeElem
     * @returns {number}
     */
    getActiveIndex(activeElem) {
        return this.flattenOptions.findIndex((option) => option.bxfId ===
            activeElem.closest(`.${BxfSelectConstants.STRING.OPTION}`)
                .dataset.bxfId);
    }
    /**
     * Reset roving tab index on rerender (make first option keyboard focusable)
     *  If flattenOptions is empty, do nothing
     */
    resetRovingTabIndex() {
        if (!isEmpty(this.flattenOptions)) {
            if (this.delegate.hasInitialOptionPlaceholder &&
                this.initialOptionRef) {
                this.initialOptionRef.tabIndex = 0;
            }
            else {
                // make first option keyboard focusable
                const firstOption = this.flattenOptions[0];
                const firstOptionElem = this.getElemForOption(firstOption);
                if (firstOptionElem) {
                    firstOptionElem.tabIndex = 0;
                }
            }
        }
    }
    renderPill(displayValue, hasTooltip = true) {
        return (h("bxf-pill", { displayValue: displayValue, size: this.pillSize, isDisabled: this.delegate.isDisabled || this.delegate.isReadOnly, deleteLabel: this.delegate.deleteLabel, lockedLabel: this.delegate.lockedLabel, hasTooltip: hasTooltip, onMouseDown: this.onPillMouseDownHandler, onClickClear: this.onPillClickClearHandler, dataTestSuffix: this.delegate.dataTestSuffix, keepMouseFocus: this.delegate.keepMouseFocus }));
    }
    renderTooltipContent() {
        const spanStyle = {
            display: 'block',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'margin-bottom': '0.5rem',
        };
        const overflow = this.selectedOptions.length - TOOLTIP_CONTENT_MAX;
        const trailingOption = h("span", null, `+ ${overflow} more`);
        const content = this.selectedOptions
            ?.slice(0, TOOLTIP_CONTENT_MAX)
            .map((option, idx) => {
            const isLastItem = idx === this.selectedOptions.length - 1 && overflow < 1;
            return (h("span", { style: isLastItem ? null : spanStyle }, option.displayValue));
        });
        if (content && overflow > 0) {
            content.push(trailingOption);
        }
        return content;
    }
    renderTooltipWithSelectedOptions(displayValue) {
        return (h("bxf-tooltip", null,
            h("div", { slot: 'target' }, this.renderPill(displayValue, false)),
            h("div", { slot: CONTENT }, this.renderTooltipContent())));
    }
    renderInfo(displayValue = `${this.selectedOptions.length} ${this.delegate.selectedLabel}`) {
        let value = displayValue;
        if (this.selectedOptions.length > 0) {
            if (this.delegate.displayAllSelected) {
                value = this.getDisplayValue(true, value);
            }
            return (h("div", { class: BxfSelectConstants.STRING.INFO_CONTAINER }, this.delegate.displayPillTooltip
                ? this.renderTooltipWithSelectedOptions(value)
                : this.renderPill(value)));
        }
        return null;
    }
    initSelection() { }
    /**
     * Get additional assistive text for value so that select component reads
     *  <label> <value> instead of <label> and entire pillbox
     */
    getValueAssistiveText() {
        return this.selectedOptions.length > 0
            ? this.getDisplayValue()
            : this.delegate.inputValue;
    }
    getDisplayValue(shortenValue = false, displayValue = `${this.selectedOptions.length} ${this.delegate.selectedLabel}`) {
        let value = displayValue;
        if (this.delegate.displayAllSelected &&
            this.selectedOptions.length === this.delegate.optionsCount) {
            value =
                shortenValue && this.delegate.type === 'filter-multiple'
                    ? this.delegate.all
                    : this.delegate.allSelected;
        }
        return value;
    }
    renderActionButton() {
        if (this.delegate.applyButton &&
            !BxfSelectDropdownLoading.isVisible(this.delegate.isLoading)) {
            return (h("div", { ref: this.actionButtonRefHandler, class: BxfSelectConstants.STRING.ACTION_BUTTON_CONTAINER },
                h("bxf-button", { onClick: this.actionButtonOnClickHandler, isDisabled: this.delegate.applyButtonIsDisabled, label: this.delegate.applyButtonLabel, size: BxfButtonSizeEnum.SMALL, dataTestSuffix: this.delegate.dataTestSuffix, keepMouseFocus: this.delegate.keepMouseFocus })));
        }
        return null;
    }
    /**
     * Render wrapper for input and dropdown
     */
    renderInputWrapper() {
        return (h("div", { class: BxfSelectInputWrapperClassName.getInputWrapperClassName(this.delegate.isTimeScale, this.delegate.isLeftLabel) },
            this.renderInstructionalText(),
            this.renderInputContainer(),
            this.renderInfo(),
            this.renderErrorMessageWrapper(),
            this.renderDropdownContainer(),
            this.renderLiveRegion()));
    }
}

class BxfSelectFilterMultiple extends BxfSelectMultiple {
    delegate;
    _shouldOpenOnFocus = true;
    initialDropdownHeight = undefined;
    hasInitialOptionPlaceholder = false;
    constructor(delegate) {
        super(delegate);
        this.delegate = delegate;
        this.hasInitialOptionPlaceholder = delegate.hasInitialOptionPlaceholder;
        this.inputOnFocusHandler = this.inputOnFocusHandler.bind(this);
        this.inputOnBlurHandler = this.inputOnBlurHandler.bind(this);
        this.inputOnClickHandler = this.inputOnClickHandler.bind(this);
        this.inputOnChangeHandler = this.inputOnChangeHandler.bind(this);
        this.inputOnMouseDownHandler = this.inputOnMouseDownHandler.bind(this);
        this.inputKeyDownHandler = this.inputKeyDownHandler.bind(this);
    }
    componentDidLoad() {
        this.getMatches();
        super.componentDidLoad();
    }
    isDropdownOpenedDidChange() {
        super.isDropdownOpenedDidChange();
        this.delegate.hasInitialOptionPlaceholder =
            this.hasInitialOptionPlaceholder &&
                (this.delegate.inputValueRaw || '').trim().length === 0;
    }
    /**
     * Overridden in filter selectbox - aria-expanded not allowed on textbox
     */
    shouldToggleExpanded() {
        return false;
    }
    /**
     * Move focus to dropdown only if icon clicked
     */
    dropdownShouldMoveFocus() {
        return !this.delegate.inputFocus;
    }
    computeDropdownHeight() {
        let height = super.computeDropdownHeight();
        if (this.initialDropdownHeight === undefined) {
            this.initialDropdownHeight = height;
        }
        if (height > this.delegate.maxDropdownHeight) {
            height = this.delegate.maxDropdownHeight;
        }
        else if (!this.delegate.hasInitialOptionPlaceholder) {
            height -= this.computeInitialOptionHeight(); // removing space for the initial option
        }
        if (BxfSelectDropdownLoading.isVisible(this.delegate.isLoading)) {
            height = BxfSelectDropdownLoading.height();
        }
        return height;
    }
    get minWidth() {
        return `${161 / 16}rem`;
    }
    /**
     * Override overlay options: do not use `:focus-visible` for text input
     */
    get overlayOptions() {
        return {
            ...super.overlayOptions,
            useFocusVisible: false,
        };
    }
    didClear(event, inputRawClear = false) {
        this.delegate.inputValueRaw = '';
        this.inputRef.value = '';
        // Using the saved initial height of the select box's overlay
        if (this.dropdownContainerRef?.style) {
            this.dropdownContainerRef.style.height = `${this.initialDropdownHeight}px`;
        }
        this.delegate.hasInitialOptionPlaceholder =
            this.hasInitialOptionPlaceholder;
        this.clearMatches();
        // Will only set the 'Select All' option when hasInitialOptionPlaceholder prop is true
        if (this.hasInitialOptionPlaceholder) {
            this.setInitialOptionState();
        }
        this.createDropdownContent();
        this._shouldOpenOnFocus = false;
        if (event && !inputRawClear) {
            this.delegate.clearButtonClicked.emit({
                srcEvent: event,
                value: '',
            });
        }
        inputRawClear || this.inputRef.focus();
    }
    clearMatches() {
        this.delegate.data.forEach((optionGroup) => optionGroup.values.forEach((option) => (option.bxfMatch = undefined)));
    }
    getInputContainerClasses() {
        const { INPUT_CONTAINER_FOCUS, INPUT_CONTAINER_KEY_FOCUS } = BxfSelectConstants.STRING;
        const focus = this.delegate.inputFocus ? INPUT_CONTAINER_FOCUS : '';
        const invalid = BxfSelectErrorClassName.getErrorClassName(this.delegate.isValid, this.delegate.validationType);
        const keyFocus = this.delegate.inputKeyFocus
            ? INPUT_CONTAINER_KEY_FOCUS
            : '';
        return `${super
            .getInputContainerClasses()
            .replace(invalid, '')
            .replace(BXF_FOCUS_KEYBOARD_ONLY, '')} ${focus} ${invalid} ${keyFocus} ${this.focusSourceCss}`;
    }
    focusInput() {
        this.toggleFocusSource(FocusSource.Mouse);
        this.inputOnFocus(true);
        this.inputRef?.focus();
    }
    inputOnFocusHandler(event) {
        this.inputOnFocus(event.type === 'focus');
    }
    inputOnFocus(hasFocus) {
        this.delegate.inputFocus = hasFocus;
        this.delegate.inputKeyFocus = hasFocus;
        this.applyFocusStylingToInputContainer();
        if (hasFocus &&
            this._shouldOpenOnFocus &&
            !this.delegate.isReadOnly &&
            !this.delegate.isOpen) {
            this.openDropdown();
        }
        else if (!this._shouldOpenOnFocus) {
            this._shouldOpenOnFocus = true;
        }
    }
    inputOnBlurHandler() {
        this.delegate.inputFocus = false;
        this.delegate.inputKeyFocus = false;
        this.removeFocusStylingFromInputContainer();
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    inputOnClickHandler(_) {
        this.setDropdownWidth();
    }
    get flattenData() {
        return flattenDataWithFilter(this.delegate.data);
    }
    inputOnChangeHandler(event) {
        this.delegate.inputValueRaw = event.target.value;
        this.delegate.hasInitialOptionPlaceholder =
            this.hasInitialOptionPlaceholder &&
                (this.delegate.inputValueRaw || '').trim().length === 0;
        if (!this.delegate.isOpen) {
            this.openDropdown();
        }
        this.delegate.delayedSearch(event.target.value);
        super.getMatches();
        this.updateLiveRegionText();
        this.resetRovingTabIndex();
        this.createDropdownContent();
        this.setDropdownContainerHeight();
        this.updateOverlay();
    }
    inputOnMouseDownHandler(event) {
        this.mouseDownToggleListener();
        if (this.delegate.inputFocus ||
            (this.overlay && this.overlay.isOpen())) {
            event.stopPropagation();
        }
        this.inputOnClickHandler(event);
    }
    inputRefHandler(elm) {
        if (elm) {
            this.inputRef = elm;
            this.inputRef.addEventListener(WindowEventMap.mousedown, this.inputOnMouseDownHandler);
            this.inputRef.addEventListener(WindowEventMap.keydown, this.inputKeyDownHandler);
            this.inputRef.addEventListener(WindowEventMap.input, this.inputOnChangeHandler);
            this.inputRef.addEventListener(WindowEventMap.click, this.inputOnClickHandler);
            this.inputRef.addEventListener(WindowEventMap.focus, this.inputOnFocusHandler);
            this.inputRef.addEventListener(WindowEventMap.blur, this.inputOnBlurHandler);
            this.inputRef.addEventListener(WindowEventMap.touchstart, this.touchStartToggleListener);
            this.delegate.clearDelegate = new BxfTextInputClear(this);
        }
    }
    getInputValueRaw() {
        return this.delegate.inputValueRaw;
    }
    get inputClasses() {
        return `${super.inputClasses} ${BxfSelectConstants.STRING.INPUT_COMBOBOX}`;
    }
    /**
     * Render assistive text visible to Screen Readers for all select types EXCEPT
     * 	filter. Filter does not require labelledby as
     * 	textbox only labeled by label/assistiveText prop
     */
    renderAssistiveText() {
        return null;
    }
    renderInput() {
        const value = this.getInputValueRaw();
        /**
         * NOTE: aria-expanded and aria-autocomplete not allowed on plain <input>,
         * 	so only using aria-autocomplete and aria-haspopup.
         *
         * https://accessibility.huit.harvard.edu/technique-aria-autocomplete
         */
        return (h("input", { ...getLabelProps(this.delegate.label, this.delegate.assistiveText, this._labelId), ref: this.inputRefHandler, placeholder: this.delegate.inputValue, class: this.inputClasses, value: value, title: this.delegate.hasTooltip ? this.delegate.inputValue : '', disabled: this.delegate.isDisabled, readOnly: this.delegate.isReadOnly, "aria-haspopup": this.ariaHasPopup, "aria-describedby": this.getAriaDescribedBy(), "data-bxf-size": this.delegate.size }));
    }
    // Filter-multiple specific
    optionOnClickHandler(event) {
        super.optionOnClickHandler(event);
    }
    isInputValueRawChangeHandler() {
        if (isEmpty(this.delegate.inputValueRaw)) {
            this.didClear(null, true);
        }
    }
    createOption(optionData, options) {
        return super.createOption(optionData, {
            ...options,
            childEl: BxfSelectOptionHighlight.createOptionContent(optionData, undefined, this.delegate.highlightMode, false, this.focusSourceCss, this.delegate.hasTooltip),
        });
    }
    separatorClasses(hidden = false) {
        const { SEPARATOR, SEPARATOR_FOCUS, SEPARATOR_HIDDEN, SEPARATOR_SMALL, SEPARATOR_LARGE, SEPARATOR_KEY_FOCUS, } = BxfSelectConstants.STRING;
        const separatorFocus = this.delegate.inputFocus ? SEPARATOR_FOCUS : '';
        const separatorHidden = hidden && this.delegate.inputValueRaw === ''
            ? SEPARATOR_HIDDEN
            : '';
        const separatorSmall = this.delegate.size === 'small' ? SEPARATOR_SMALL : '';
        const separatorLarge = this.delegate.size === 'large' ? SEPARATOR_LARGE : '';
        const separatorKeyFocus = this.isMouseFocus ? '' : SEPARATOR_KEY_FOCUS;
        const separatorError = BxfSelectErrorClassName.getSeparatorErrorClassName(this.delegate.isValid, this.delegate.validationType);
        return `${SEPARATOR} ${separatorSmall} ${separatorLarge} ${separatorFocus} ${separatorHidden} ${separatorKeyFocus} ${separatorError}`;
    }
    /**
     * Handle up/down arrow on combobox input
     */
    inputVerticalArrowHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.delegate.isOpen) {
            this.openDropdown();
        }
        this.updateOverlay();
        this.dummyCallFlag = false;
        this.dropdownDummyArrowCallback(event);
    }
    /**
     * Handle the following keyboard events on text input:
     *  - Down arrow/up arrow: open dropdown and focus first/last option
     * 	- Tab - change focus sources, close dropdown if tabbing away
     *  - All other keys: default event
     */
    inputKeyDownHandler(event) {
        if (FocusIndexManager.VERTICAL_NAV_KEYS.includes(event.key)) {
            this.inputVerticalArrowHandler(event);
        }
        else if (event.key === KeyboardEventKey.Tab) {
            this.toggleFocusSource(FocusSource.Keyboard);
            if (!this.delegate.isReadOnly && this.delegate.isOpen) {
                this.closeDropdown();
                this._shouldOpenOnFocus = true;
            }
        }
    }
    renderSeparator(hidden = false) {
        return (h("div", { class: BxfSelectConstants.STRING.SEPARATOR_CONTAINER },
            h("div", { class: this.separatorClasses(hidden) })));
    }
    renderIcon() {
        return [
            this.renderSeparator(),
            h("bxf-icon", { class: this.iconClasses(), state: 'primary', type: 'data-select-caret-down', dataTestSuffix: this.delegate.dataTestSuffix }),
        ];
    }
    renderInputSeparator() {
        return this.selectedOptions.length > 0 && this.renderSeparator(true);
    }
    renderInputClearContainer() {
        return (h("div", { class: BxfTextInputConstants.STRING.INPUT_CLEAR_CONTAINER, "data-bxf-clear-icon-visible": String(this.delegate.clearDelegate &&
                this.delegate.clearDelegate.shouldDisplayIcon()) },
            this.renderInput(),
            this.delegate.inputValueRaw !== '' &&
                this.delegate.clearDelegate?.render(this.delegate.inputValueRaw !== '')));
    }
    renderInputContainerContent() {
        return [
            this.renderInputClearContainer(),
            this.renderInputSeparator(),
            this.renderInfo(`${this.selectedOptions.length}`),
            this.renderIcon(),
        ];
    }
    /**
     * Handle keydown events for dropdown
     * @param {KeyboardEvent} event
     */
    dropdownKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.Escape) {
            this._shouldOpenOnFocus = false;
            this.resetFocusToInput();
        }
        else {
            super.dropdownKeyDownHandler(event);
        }
    }
    /**
     * Handle keydown for initial option
     * @protected
     * @param {KeyboardEvent} event
     */
    initialOptionKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.Escape) {
            this.resetFocusToInput();
        }
        else {
            super.initialOptionKeyDownHandler(event);
        }
    }
    /**
     * Override simple dropdown button with element with role combobox
     * @protected
     */
    renderInputContainer() {
        return (h("div", { ref: this.targetRefHandler, class: this.getInputContainerClasses(), onClick: this.inputContainerOnClickHandler, onMouseDown: this.mouseDownToggleListener }, this.renderInputContainerContent()));
    }
    /**
     * Alert screen reader users when results found
     * @protected
     */
    renderLiveRegion() {
        return (h("div", { "aria-live": LiveRegionPoliteness.POLITE, role: Role.STATUS, class: BXF_ASSISTIVE_TEXT_HIDDEN }, this.liveRegionText));
    }
    /**
     * Render wrapper for input and dropdown
     */
    renderInputWrapper() {
        return (h("div", { class: BxfSelectInputWrapperClassName.getInputWrapperClassName(this.delegate.isTimeScale, this.delegate.isLeftLabel) },
            this.renderInstructionalText(),
            this.renderInputContainer(),
            this.renderErrorMessageWrapper(),
            this.renderDropdownContainer(),
            this.renderLiveRegion()));
    }
}

class BxfSelectFilterSimple extends BxfSelectSimple {
    delegate;
    _shouldOpenOnFocus = true;
    constructor(delegate) {
        super(delegate);
        this.delegate = delegate;
        this.inputOnFocusHandler = this.inputOnFocusHandler.bind(this);
        this.inputOnBlurHandler = this.inputOnBlurHandler.bind(this);
        this.inputOnClickHandler = this.inputOnClickHandler.bind(this);
        this.inputOnChangeHandler = this.inputOnChangeHandler.bind(this);
        this.inputOnMouseDownHandler = this.inputOnMouseDownHandler.bind(this);
        this.inputKeyDownHandler = this.inputKeyDownHandler.bind(this);
    }
    componentDidLoad() {
        super.getMatches();
        super.componentDidLoad();
    }
    get minWidth() {
        return `${98 / 16}rem`;
    }
    /**
     * Overridden in filter selectbox - aria-expanded not allowed on textbox
     */
    shouldToggleExpanded() {
        return false;
    }
    /**
     * Override overlay options: do not use `:focus-visible` for text input
     */
    get overlayOptions() {
        return {
            ...super.overlayOptions,
            useFocusVisible: false,
        };
    }
    didClear(event) {
        this.delegate.inputValueRaw = '';
        this.inputRef.value = '';
        this.clearMatches();
        this.createDropdownContent();
        this._shouldOpenOnFocus = false;
        if (event) {
            this.delegate.clearButtonClicked.emit({
                srcEvent: event,
                value: '',
            });
            this.delegate.inputFocus = true;
            this.inputRef.focus();
        }
    }
    clearMatches() {
        this.delegate.data.forEach((optionGroup) => optionGroup.values.forEach((option) => {
            option.bxfMatch = undefined;
        }));
        this.liveRegionText = '';
    }
    optionOnClickHandler = debounce((event) => {
        super.optionOnClickHandler(event);
        this.didClear();
        if (!this.isMouseFocus || this.delegate.keepMouseFocus) {
            this.resetFocusToInput();
        }
    }, 10);
    getInputContainerClasses() {
        const { INPUT_CONTAINER_FOCUS, INPUT_CONTAINER_KEY_FOCUS } = BxfSelectConstants.STRING;
        const focus = this.delegate.inputFocus ? INPUT_CONTAINER_FOCUS : '';
        const invalid = BxfSelectErrorClassName.getErrorClassName(this.delegate.isValid, this.delegate.validationType);
        const keyFocus = this.delegate.inputKeyFocus
            ? INPUT_CONTAINER_KEY_FOCUS
            : '';
        return `
			${super
            .getInputContainerClasses()
            .replace(invalid, '')
            .replace(BXF_FOCUS_KEYBOARD_ONLY, '')}
			${focus}
			${invalid}
			${keyFocus}
			${this.focusSourceCss}`;
    }
    focusInput() {
        this.toggleFocusSource(FocusSource.Mouse);
        this.inputOnFocus(true);
        this.inputRef?.focus();
    }
    inputOnFocusHandler(event) {
        this.inputOnFocus(event.type === 'focus');
    }
    inputOnFocus(hasFocus) {
        this.delegate.clearDelegate.clear();
        this.delegate.inputFocus = hasFocus;
        this.delegate.inputKeyFocus = hasFocus;
        this.applyFocusStylingToInputContainer();
        if (hasFocus &&
            this._shouldOpenOnFocus &&
            !this.delegate.isReadOnly &&
            !this.delegate.isOpen) {
            this.openDropdown();
        }
        else if (!this._shouldOpenOnFocus) {
            this._shouldOpenOnFocus = true;
        }
    }
    inputOnBlurHandler() {
        this.delegate.inputFocus = false;
        this.delegate.inputKeyFocus = false;
        this.removeFocusStylingFromInputContainer();
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    inputOnClickHandler(_) {
        this.setDropdownWidth();
    }
    get flattenData() {
        return flattenDataWithFilter(this.delegate.data);
    }
    /**
     * Handle up/down arrow on combobox input
     */
    inputVerticalArrowHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.delegate.isOpen) {
            this.openDropdown();
        }
        this.updateOverlay();
        this.dummyCallFlag = false;
        this.dropdownDummyArrowCallback(event);
    }
    /**
     * Handle the following keyboard events on text input:
     *  - Down arrow/up arrow: open dropdown and focus first/last option
     * 	- Tab - change focus sources, close dropdown if tabbing away
     *  - All other keys: default event
     */
    inputKeyDownHandler(event) {
        if (FocusIndexManager.VERTICAL_NAV_KEYS.includes(event.key) &&
            !this.delegate.isReadOnly) {
            this.inputVerticalArrowHandler(event);
        }
        else if (event.key === KeyboardEventKey.Tab) {
            this.toggleFocusSource(FocusSource.Keyboard);
            if (!this.delegate.isReadOnly && this.delegate.isOpen) {
                this.closeDropdown();
                this._shouldOpenOnFocus = true;
            }
        }
    }
    /**
     * Handle keydown events for dropdown
     * @param {KeyboardEvent} event
     */
    dropdownKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.Escape) {
            this._shouldOpenOnFocus = false;
            this.resetFocusToInput();
        }
        else {
            super.dropdownKeyDownHandler(event);
        }
    }
    /**
     * Handle keydown for initial option
     * @protected
     * @param {KeyboardEvent} event
     */
    initialOptionKeyDownHandler(event) {
        if (event.key === KeyboardEventKey.Escape) {
            this._shouldOpenOnFocus = false;
            this.resetFocusToInput();
        }
        else {
            super.initialOptionKeyDownHandler(event);
        }
    }
    /**
     * Move focus to dropdown only if icon clicked
     */
    dropdownShouldMoveFocus() {
        return !this.delegate.inputFocus;
    }
    inputOnChangeHandler(event) {
        this.delegate.inputValueRaw = event.target.value;
        if (!this.delegate.isOpen) {
            this.openDropdown();
        }
        this.delegate.delayedSearch(event.target.value);
        this.getMatches();
        this.updateLiveRegionText();
        this.createDropdownContent();
        this.setDropdownContainerHeight();
        this.overlay?.updatePosition();
    }
    createOption(optionData) {
        return super.createOption(optionData, {
            childEl: BxfSelectOptionHighlight.createOptionContent(optionData, this.delegate.size, this.delegate.highlightMode, true, this.focusSourceCss, this.delegate.hasTooltip),
        });
    }
    separatorClasses() {
        const { SEPARATOR, SEPARATOR_FOCUS, SEPARATOR_SMALL, SEPARATOR_LARGE, SEPARATOR_KEY_FOCUS, } = BxfSelectConstants.STRING;
        const separatorFocus = this.delegate.inputFocus ? SEPARATOR_FOCUS : '';
        const separatorSmall = this.delegate.size === 'small' ? SEPARATOR_SMALL : '';
        const separatorLarge = this.delegate.size === 'large' ? SEPARATOR_LARGE : '';
        const separatorKeyFocus = this.isMouseFocus ? '' : SEPARATOR_KEY_FOCUS;
        const separatorError = BxfSelectErrorClassName.getSeparatorErrorClassName(this.delegate.isValid, this.delegate.validationType);
        return `${SEPARATOR} ${separatorSmall} ${separatorLarge} ${separatorFocus} ${separatorKeyFocus} ${separatorError}`;
    }
    renderSeparator() {
        return (h("div", { class: BxfSelectConstants.STRING.SEPARATOR_CONTAINER },
            h("div", { class: this.separatorClasses() })));
    }
    renderIcon() {
        return [
            this.renderSeparator(),
            h("bxf-icon", { class: this.iconClasses(), state: 'primary', type: 'data-select-caret-down', dataTestSuffix: this.delegate.dataTestSuffix }),
        ];
    }
    inputOnMouseDownHandler(event) {
        this.mouseDownToggleListener();
        if (this.delegate.inputFocus ||
            (this.overlay && this.overlay.isOpen())) {
            event.stopPropagation();
        }
        this.inputOnClickHandler(event);
    }
    inputRefHandler(elm) {
        if (elm) {
            this.inputRef = elm;
            this.inputRef.addEventListener(WindowEventMap.mousedown, this.inputOnMouseDownHandler);
            this.inputRef.addEventListener(WindowEventMap.keydown, this.inputKeyDownHandler);
            this.inputRef.addEventListener(WindowEventMap.input, this.inputOnChangeHandler);
            this.inputRef.addEventListener(WindowEventMap.click, this.inputOnClickHandler);
            this.inputRef.addEventListener(WindowEventMap.focus, this.inputOnFocusHandler);
            this.inputRef.addEventListener(WindowEventMap.blur, this.inputOnBlurHandler);
            this.inputRef.addEventListener(WindowEventMap.keydown, this.tabKeyToggleListener);
            this.inputRef.addEventListener(WindowEventMap.touchstart, this.touchStartToggleListener, { passive: true });
            this.delegate.clearDelegate = new BxfTextInputClear(this);
        }
    }
    renderInputContainerContent() {
        return [this.renderInputClearContainer(), this.renderIcon()];
    }
    renderClearDelegate() {
        return (this.delegate.clearDelegate &&
            this.delegate.clearDelegate.render(this.delegate.inputValueRaw !== '' && this.delegate.inputFocus, undefined, undefined, this.delegate.clearButtonLabel));
    }
    renderInputClearContainer() {
        const showIcon = !this.delegate.isDisabled;
        return (h("div", { class: BxfTextInputConstants.STRING.INPUT_CLEAR_CONTAINER, "data-bxf-clear-icon-visible": String(showIcon && this.delegate.clearDelegate?.shouldDisplayIcon()) },
            this.renderInput(),
            showIcon ? this.renderClearDelegate() : null));
    }
    getInputValueRaw() {
        return this.delegate.inputValueRaw;
    }
    /**
     * Alert screen reader users when results found
     * @protected
     */
    renderLiveRegion() {
        return (h("div", { "aria-live": LiveRegionPoliteness.POLITE, role: Role.STATUS, class: BXF_ASSISTIVE_TEXT_HIDDEN }, this.liveRegionText));
    }
    /**
     * Override simple dropdown button with element with role combobox
     * @protected
     */
    renderInputContainer() {
        return (h("div", { ref: this.targetRefHandler, class: this.getInputContainerClasses(), onClick: this.inputContainerOnClickHandler, onMouseDown: this.mouseDownToggleListener }, this.renderInputContainerContent()));
    }
    get inputClasses() {
        const disabledClass = this.delegate.isDisabled
            ? BxfSelectConstants.STRING.INPUT_DISABLED
            : '';
        return `${super.inputClasses} ${BxfSelectConstants.STRING.INPUT_COMBOBOX} ${disabledClass}`;
    }
    /**
     * Render assistive text visible to Screen Readers for all select types EXCEPT
     * 	filter. Filter does not require labelledby as
     * 	textbox only labeled by label/assistiveText prop
     */
    renderAssistiveText() {
        return null;
    }
    renderInput() {
        const value = this.getInputValueRaw();
        /**
         * NOTE: aria-expanded and aria-autocomplete not allowed on plain <input>,
         * 	so only using aria-haspopup.
         *
         * https://accessibility.huit.harvard.edu/technique-aria-autocomplete
         */
        return (h("input", { ...getLabelProps(this.delegate.label, this.delegate.assistiveText, this._labelId), ref: this.inputRefHandler, placeholder: this.delegate.inputValue, class: this.inputClasses, value: value, title: this.delegate.hasTooltip ? this.delegate.inputValue : '', disabled: this.delegate.isDisabled, readOnly: this.delegate.isReadOnly, "aria-haspopup": this.ariaHasPopup, "aria-describedby": this.getAriaDescribedBy(), "data-bxf-size": this.delegate.size }));
    }
}

class BxfSelectTabularUiUtil {
    //#region "Data for table"
    static getKeyColumn(data = []) {
        return data.find((optionGroup) => optionGroup.isKey) || data[0];
    }
    static getKeyColumnIndex(data = []) {
        const index = data.findIndex((optionGroup) => optionGroup.isKey);
        return Math.max(index, 0);
    }
    static getTableRows(data = []) {
        const rows = [];
        const dataRecords = (data[0] || []).values;
        for (let numberOfRows = 0; numberOfRows < dataRecords.length; numberOfRows++) {
            const row = [];
            data.forEach((optionGroup) => row.push(optionGroup.values[numberOfRows] || {}));
            rows.push(row);
        }
        return rows;
    }
}

class BxfSelectTabular extends BxfSelectFilterSimple {
    delegate;
    constructor(delegate) {
        super(delegate);
        this.delegate = delegate;
        this.onClickOption = this.onClickOption.bind(this);
        this.onRowKeyDown = this.onRowKeyDown.bind(this);
    }
    hasMatch = false;
    renderDropdownContainer() {
        this.hasMatch = this.flattenOptions.some((match) => match.bxfMatch);
        return (h("div", { class: BxfSelectConstants.STRING.TYPEAHEAD_CONTAINER, ref: this.sourceRefHandler, onMouseDown: (event) => event.stopPropagation(), onKeyDown: this.dropdownKeyDownHandler }, [this.renderLoading(), this.renderContent()]));
    }
    renderHeaderCell(label) {
        return (h("th", { class: BxfSelectConstants.STRING.TABLE_HEADER_CELL, style: { textAlign: 'left' } }, label));
    }
    renderHeader() {
        return (h("thead", null,
            h("tr", null, this.delegate.data.map((optionGroup) => this.renderHeaderCell(optionGroup.label)))));
    }
    renderCell(option, match = true) {
        let content = option.displayValue;
        if (match) {
            content = BxfTypeAheadUiUtil.hightlightOption(option, this.delegate.highlightMode);
        }
        return (h("td", { role: Role.GRIDCELL, class: BxfSelectConstants.STRING.TABLE_CELL }, content));
    }
    onRowKeyDown(event) {
        if (FocusIndexManager.PRESS_KEYS.includes(event.key)) {
            this.onClickOption(event);
        }
    }
    onClickOption(event) {
        const bxfId = event.currentTarget?.dataset?.bxfId;
        const selectedOption = flatten(map(this.delegate.data, 'values')).find((option) => option.bxfId === bxfId);
        if (selectedOption) {
            selectedOption.isSelected = true;
            this.delegate.setSelected(selectedOption, true, event);
            this.delegate.inputValue = selectedOption.displayValue;
        }
        this.closeDropdown();
        this.resetFocusToInputContainer();
        this.didClear();
        if (!this.isMouseFocus) {
            this.resetFocusToInput();
        }
        this.dummyCallFlag = false;
    }
    getTabularClasses() {
        return getKeyboardFocusCSS(BxfSelectConstants.STRING.TABLE_ROW, this.delegate.keepMouseFocus);
    }
    get showDropdownInfo() {
        return (!this.delegate.isLoading &&
            !isEmpty(this.delegate.inputValueRaw) &&
            !this.hasMatch);
    }
    renderRow(rowData, rowIndex) {
        if (!rowData.find((cell) => cell.bxfMatch) &&
            !isEmpty(this.delegate.inputValueRaw) &&
            !this.showDropdownInfo)
            return null;
        return (h("tr", { class: this.getTabularClasses(), "data-bxf-id": rowData[BxfSelectTabularUiUtil.getKeyColumnIndex(this.delegate.data)].bxfId, "data-bxf-row-metadata": rowIndex, onClick: this.onClickOption, onKeyDown: this.onRowKeyDown, tabIndex: -1 }, rowData.map((cell) => this.renderCell(cell))));
    }
    renderBody() {
        return (h("tbody", null, BxfSelectTabularUiUtil.getTableRows(this.delegate.data)
            .map((row, rowIndex) => this.renderRow(row, rowIndex))
            .filter(Boolean)
            .slice(0, get(BxfSelectTabularUiUtil.getKeyColumn(this.delegate.data), 'limit', BxfSelectTabularUiUtil.getTableRows(this.delegate.data).length))));
    }
    totalResults() {
        let totalResults = 0;
        const keyColumn = BxfSelectTabularUiUtil.getKeyColumn(this.delegate.data);
        BxfSelectTabularUiUtil.getTableRows(this.delegate.data).forEach((row) => {
            const tableRow = row.find((cell) => cell.bxfMatch);
            if (tableRow) {
                totalResults += 1;
            }
        });
        // Do not display if limit and total results are equal
        if (totalResults <= get(keyColumn, 'limit')) {
            totalResults = undefined;
        }
        // Do not display if limit is undefined
        if (get(keyColumn, 'limit') === undefined) {
            totalResults = undefined;
        }
        // Overwrite with user input
        if (get(keyColumn, 'totalResults') != null) {
            totalResults = keyColumn.totalResults;
        }
        return totalResults >= 0 ? totalResults : undefined;
    }
    renderTable() {
        return (h("table", { class: BxfSelectConstants.STRING.TABLE, role: Role.GRID, "aria-label": this.delegate.baseLabel },
            this.renderHeader(),
            this.renderBody()));
    }
    getElemForOption(option) {
        return this.dropdownContainerRef?.querySelector(`[data-bxf-id="${option.bxfId}"]`);
    }
    activeResults() {
        return flatten([
            Array.from(this.dropdownContainerRef.querySelectorAll(`.${BxfSelectConstants.STRING.OPTION}`)),
            Array.from(this.dropdownContainerRef.querySelectorAll(`.${BxfSelectConstants.STRING.TABLE_ROW}`)),
        ]);
    }
    getActiveIndex(activeElem) {
        return this.activeResults().findIndex((option) => get(option, ['dataset', 'bxfId']) === activeElem.dataset.bxfId);
    }
    get ariaHasPopup() {
        return this.delegate.isReadOnly
            ? HasPopupValue.FALSE
            : HasPopupValue.GRID;
    }
    inputOnChangeHandler(event) {
        super.inputOnChangeHandler(event);
        if (this.delegate.isLoading) {
            this.overlay?.updatePosition();
        }
    }
    isInputValueRawChangeHandler() {
        if (!isEmpty(this.delegate.inputValueRaw)) {
            this.overlay?.updatePosition();
        }
    }
    changeFocusToIndex(index) {
        if (this.delegate.hasInitialOptionPlaceholder) {
            this.resetInitialTabIndex();
        }
        const results = this.activeResults();
        const newFocusElem = results[index];
        if (newFocusElem) {
            newFocusElem.tabIndex = 0;
            newFocusElem.focus();
        }
        else if (index === results.length) {
            this.navigateToIndex(0, this.delegate.hasInitialOptionPlaceholder &&
                this.initialOptionRef);
        }
        else {
            this.navigateToIndex(results.length - 1, this.flattenOptions.length === 0);
        }
    }
    renderContent() {
        const { data, isLoading } = this.delegate;
        if (isLoading || isNil(data))
            return null;
        return (h("div", { class: BxfSelectConstants.STRING.DROPDOWN_CONTENT, "data-bxf-is-loading": String(this.delegate.isLoading) }, data.length > 0 && this.renderTable()));
    }
}

const bxfSelectCss = "button.sc-bxf-select:hover,a.sc-bxf-select:hover,input[type=radio].sc-bxf-select:hover,input[type=checkbox].sc-bxf-select:hover,input[type=radio].sc-bxf-select:checked:hover,input[type=checkbox].sc-bxf-select:checked:hover,svg.sc-bxf-select:hover{cursor:pointer}svg[disabled].sc-bxf-select:hover,button[disabled].sc-bxf-select:hover,label[disabled].sc-bxf-select:hover,input[type=checkbox].sc-bxf-select:disabled:hover,input[type=radio].sc-bxf-select:disabled:hover,input[type=checkbox].sc-bxf-select:checked:disabled:hover,input[type=radio].sc-bxf-select:checked:disabled:hover{cursor:default}.bxf__assistive-text--hidden.sc-bxf-select{position:absolute !important;left:-625rem;top:auto;width:0.0625rem;height:0.0625rem;overflow:hidden}.oneds-blk-heading-display-1.sc-bxf-select{font-size:clamp(2.3675rem, 1.3308rem + 4.6074vw, 5.4775rem);line-height:1.22;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-display-1.sc-bxf-select{line-height:1.21}}@media (min-width: 48rem){.oneds-blk-heading-display-1.sc-bxf-select{line-height:1.2}}@media (min-width: 64rem){.oneds-blk-heading-display-1.sc-bxf-select{line-height:1.18}}@media (min-width: 90rem){.oneds-blk-heading-display-1.sc-bxf-select{line-height:1.16}}.oneds-blk-heading-xxl.sc-bxf-select{font-size:clamp(2.0931rem, 1.3588rem + 3.2639vw, 4.2963rem);line-height:1.22;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-xxl.sc-bxf-select{line-height:1.22}}@media (min-width: 48rem){.oneds-blk-heading-xxl.sc-bxf-select{line-height:1.21}}@media (min-width: 64rem){.oneds-blk-heading-xxl.sc-bxf-select{line-height:1.2}}@media (min-width: 90rem){.oneds-blk-heading-xxl.sc-bxf-select{line-height:1.18}}.oneds-blk-heading-xl.sc-bxf-select{font-size:clamp(1.8506rem, 1.3444rem + 2.25vw, 3.3694rem);line-height:1.23;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-xl.sc-bxf-select{line-height:1.22}}@media (min-width: 48rem){.oneds-blk-heading-xl.sc-bxf-select{line-height:1.22}}@media (min-width: 64rem){.oneds-blk-heading-xl.sc-bxf-select{line-height:1.21}}@media (min-width: 90rem){.oneds-blk-heading-xl.sc-bxf-select{line-height:1.2}}.oneds-blk-heading-l.sc-bxf-select{font-size:clamp(1.6363rem, 1.3008rem + 1.4907vw, 2.6425rem);line-height:1.23;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-l.sc-bxf-select{line-height:1.23}}@media (min-width: 48rem){.oneds-blk-heading-l.sc-bxf-select{line-height:1.23}}@media (min-width: 64rem){.oneds-blk-heading-l.sc-bxf-select{line-height:1.22}}@media (min-width: 90rem){.oneds-blk-heading-l.sc-bxf-select{line-height:1.21}}.oneds-blk-heading-m.sc-bxf-select{font-size:clamp(1.4469rem, 1.2383rem + 0.9269vw, 2.0725rem);line-height:1.24;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-m.sc-bxf-select{line-height:1.23}}@media (min-width: 48rem){.oneds-blk-heading-m.sc-bxf-select{line-height:1.23}}@media (min-width: 64rem){.oneds-blk-heading-m.sc-bxf-select{line-height:1.23}}@media (min-width: 90rem){.oneds-blk-heading-m.sc-bxf-select{line-height:1.22}}.oneds-blk-heading-s.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.24;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}@media (min-width: 37.5rem){.oneds-blk-heading-s.sc-bxf-select{line-height:1.24}}@media (min-width: 48rem){.oneds-blk-heading-s.sc-bxf-select{line-height:1.24}}@media (min-width: 64rem){.oneds-blk-heading-s.sc-bxf-select{line-height:1.24}}@media (min-width: 90rem){.oneds-blk-heading-s.sc-bxf-select{line-height:1.23}}.oneds-blk-heading-xs.sc-bxf-select{font-size:clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);line-height:1.24;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-heading-xxs.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-eyebrow-l.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.27;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-eyebrow-m.sc-bxf-select{font-size:14px;line-height:1.3;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-eyebrow-s.sc-bxf-select{font-size:12px;line-height:1.34;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xl.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.4;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xl-bold.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.4;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xl-italic.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.4;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-xl-link.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.4;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-l.sc-bxf-select{font-size:clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);line-height:1.45;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-l-bold.sc-bxf-select{font-size:clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);line-height:1.45;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-l-italic.sc-bxf-select{font-size:clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);line-height:1.45;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-l-link.sc-bxf-select{font-size:clamp(1.1313rem, 1.0833rem + 0.213vw, 1.275rem);line-height:1.45;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-m.sc-bxf-select,.bxf-select__input-large.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-m-bold.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-m-italic.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-m-link.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-m-compact.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-m-compact-bold.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-m-compact-italic.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-m-compact-link.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-s.sc-bxf-select,.bxf-select__input.sc-bxf-select,.bxf-select__label.sc-bxf-select,.bxf-select__label-asterisk.sc-bxf-select,.bxf-select__message.sc-bxf-select,.bxf-select__message--error.sc-bxf-select,.bxf-select__message--warning.sc-bxf-select,.bxf-select__option.sc-bxf-select,.bxf-select__loading.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select,.bxf-select__table-cell.sc-bxf-select,.bxf-select__table-header-cell.sc-bxf-select,.bxf-select__instructional-text.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select{font-size:14px;line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-s-bold.sc-bxf-select,.bxf-select__option-label-bold.sc-bxf-select{font-size:14px;line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-s-italic.sc-bxf-select{font-size:14px;line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-s-link.sc-bxf-select{font-size:14px;line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-s-compact.sc-bxf-select{font-size:14px;line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-s-compact-bold.sc-bxf-select{font-size:14px;line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-s-compact-italic.sc-bxf-select{font-size:14px;line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-s-compact-link.sc-bxf-select{font-size:14px;line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-xs.sc-bxf-select,.bxf-select__input-small.sc-bxf-select{font-size:12px;line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xs-bold.sc-bxf-select{font-size:12px;line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xs-italic.sc-bxf-select{font-size:12px;line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-xs-link.sc-bxf-select{font-size:12px;line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-blk-body-xs-compact.sc-bxf-select{font-size:12px;line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xs-compact-bold.sc-bxf-select{font-size:12px;line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif}.oneds-blk-body-xs-compact-italic.sc-bxf-select{font-size:12px;line-height:1.25;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic}.oneds-blk-body-xs-compact-link.sc-bxf-select{font-size:12px;line-height:1.25;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline}.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select{font-size:clamp(2.0931rem, 1.3588rem + 3.2639vw, 4.2963rem);line-height:1.22;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:var(--oneds-space-fluid-m) 0 var(--oneds-space-fluid-m) 0}@media (min-width: 37.5rem){.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select{line-height:1.22}}@media (min-width: 48rem){.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select{line-height:1.21}}@media (min-width: 64rem){.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select{line-height:1.2}}@media (min-width: 90rem){.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select{line-height:1.18}}.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h1.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select{font-size:clamp(1.8506rem, 1.3444rem + 2.25vw, 3.3694rem);line-height:1.23;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:var(--oneds-space-fluid-s) 0 var(--oneds-space-fluid-m) 0}@media (min-width: 37.5rem){.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select{line-height:1.22}}@media (min-width: 48rem){.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select{line-height:1.22}}@media (min-width: 64rem){.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select{line-height:1.21}}@media (min-width: 90rem){.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select{line-height:1.2}}.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h2.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select{font-size:clamp(1.6363rem, 1.3008rem + 1.4907vw, 2.6425rem);line-height:1.23;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:var(--oneds-space-fluid-xs) 0 var(--oneds-space-fluid-s) 0}@media (min-width: 37.5rem){.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select{line-height:1.23}}@media (min-width: 48rem){.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select{line-height:1.23}}@media (min-width: 64rem){.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select{line-height:1.22}}@media (min-width: 90rem){.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select{line-height:1.21}}.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h3.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select{font-size:clamp(1.4469rem, 1.2383rem + 0.9269vw, 2.0725rem);line-height:1.24;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:var(--oneds-space-fluid-xxs) 0 var(--oneds-space-fluid-xs) 0}@media (min-width: 37.5rem){.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select{line-height:1.23}}@media (min-width: 48rem){.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select{line-height:1.23}}@media (min-width: 64rem){.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select{line-height:1.23}}@media (min-width: 90rem){.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select{line-height:1.22}}.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h4.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select{font-size:clamp(1.2794rem, 1.164rem + 0.513vw, 1.6256rem);line-height:1.24;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:var(--oneds-space-fluid-xxxs) 0 var(--oneds-space-fluid-xxs) 0}@media (min-width: 37.5rem){.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select{line-height:1.24}}@media (min-width: 48rem){.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select{line-height:1.24}}@media (min-width: 64rem){.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select{line-height:1.24}}@media (min-width: 90rem){.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select{line-height:1.23}}.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h5.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select strong.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select em.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select a.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.25;word-wrap:break-word;font-weight:800;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding-bottom:var(--oneds-space-xxs)}.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select strong.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select em.sc-bxf-select a.sc-bxf-select:focus-visible,.oneds-richTextEditorOutput.sc-bxf-select h6.sc-bxf-select a.sc-bxf-select a.sc-bxf-select:focus-visible{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select p.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding-bottom:var(--oneds-space-m)}.oneds-richTextEditorOutput.sc-bxf-select ul.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select ol.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding:0 0 var(--oneds-space-m) var(--oneds-space-m);list-style-type:revert}.oneds-richTextEditorOutput.sc-bxf-select ul.sc-bxf-select li.sc-bxf-select:not(:last-child),.oneds-richTextEditorOutput.sc-bxf-select ol.sc-bxf-select li.sc-bxf-select:not(:last-child){padding-bottom:var(--oneds-space-m)}.oneds-richTextEditorOutput.sc-bxf-select strong.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;padding-bottom:var(--oneds-space-m)}.oneds-richTextEditorOutput.sc-bxf-select em.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:400;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;font-style:italic;padding-bottom:var(--oneds-space-m)}.oneds-richTextEditorOutput.sc-bxf-select a.sc-bxf-select{font-size:clamp(1rem, 1rem + 0vw, 1rem);line-height:1.5;word-wrap:break-word;font-weight:700;font-family:\"BLK Fort\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Fira Sans\", \"Helvetica Neue\", sans-serif;text-decoration:underline;color:var(--oneds-text-default);text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;transition:text-decoration 300ms ease-out}.oneds-richTextEditorOutput.sc-bxf-select .mce-content-body.sc-bxf-select [data-mce-selected=inline-boundary].sc-bxf-select{background-clip:content-box}.oneds-richTextEditorOutput.sc-bxf-select a.sc-bxf-select:hover,.oneds-richTextEditorOutput.sc-bxf-select a.sc-bxf-select:active{text-decoration:underline;text-decoration-thickness:4px;transition:text-decoration 100ms ease-out}.oneds-richTextEditorOutput.sc-bxf-select a.sc-bxf-select:focus-visible{background:var(--oneds-bg-black);color:var(--oneds-text-inverse);text-decoration:none;border:none;outline:none;border-radius:0}.oneds-richTextEditorOutput.oneds-bg-black.sc-bxf-select a.sc-bxf-select{color:var(--oneds-text-inverse)}.oneds-richTextEditorOutput.oneds-bg-black.sc-bxf-select a.sc-bxf-select:focus-visible{background:var(--oneds-bg-white);color:var(--oneds-text-default);text-decoration:none;border:none;outline:none;border-radius:0}.oneds-richTextEditorOutput.sc-bxf-select sub.sc-bxf-select,.oneds-richTextEditorOutput.sc-bxf-select sup.sc-bxf-select{line-height:0}.bxf-clear-icon.sc-bxf-select{display:flex;flex-direction:row;justify-content:center;align-items:center;height:100%;padding:0 12px;z-index:1;position:absolute;top:0;right:0;background-color:transparent;border:none}.bxf-clear-icon.sc-bxf-select:focus{outline:0.0625rem dotted #ff4713;outline:auto #ff4713}.bxf-clear-icon.bxf__focus--keyboard-only.sc-bxf-select:focus:not(:focus-visible){outline:0}.bxf-clear-icon__container.sc-bxf-select{display:inline-flex;position:relative;width:100%;height:100%}.bxf-clear-icon__container.sc-bxf-select input.sc-bxf-select{width:100%;padding:0 12px}.bxf-clear-icon__container[data-bxf-clear-icon-visible=true].sc-bxf-select input.sc-bxf-select{width:calc(100% - 3.25rem);text-overflow:ellipsis}.bxf-clear-icon__container[data-bxf-clear-icon-visible=true].sc-bxf-select input[data-bxf-size=small].sc-bxf-select{width:calc(100% - 2.5rem)}.bxf-clear-icon__container[data-bxf-clear-icon-visible=true].sc-bxf-select input[data-bxf-size=small].sc-bxf-select~.bxf-clear-icon.sc-bxf-select{padding:0 8px}.bxf-clear-icon--hidden.sc-bxf-select{visibility:hidden}.bxf-checkbox__checkbox.sc-bxf-select{box-sizing:border-box;display:flex;align-items:center;justify-content:center;border:0.0625rem solid #000000;background-color:#ffffff;outline-offset:4px}.bxf-checkbox__checkbox.sc-bxf-select bxf-icon.sc-bxf-select{display:flex}.bxf-checkbox__checkbox.bxf-checkbox__validation-error.sc-bxf-select{border-color:#c00b28;background-color:#ffffff}.bxf-checkbox__checkbox.bxf-checkbox__validation-warning.sc-bxf-select{border-color:#ff8200;background-color:#ffffff}.bxf-checkbox__regular.sc-bxf-select{height:16px;min-width:16px;width:16px}.bxf-checkbox__container.sc-bxf-select{user-select:none;box-sizing:border-box;position:relative;color:#000000;align-items:center;width:inherit;display:flex;pointer-events:auto}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select{position:absolute;opacity:0;margin:0;top:0;left:0}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{background-color:#ffffff;border:0.0625rem solid #000000}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:focus,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:focus{outline:none}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:focus~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:focus~.bxf-checkbox__checkbox.sc-bxf-select{outline:0.0625rem dotted #ff4713;outline:auto #ff4713}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:focus~.bxf-checkbox__checkbox.bxf-checkbox__validation-error.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:focus~.bxf-checkbox__checkbox.bxf-checkbox__validation-error.sc-bxf-select{outline-color:#c00b28}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:focus~.bxf-checkbox__checkbox.bxf-checkbox__validation-warning.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:focus~.bxf-checkbox__checkbox.bxf-checkbox__validation-warning.sc-bxf-select{outline-color:#ff8200}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible)~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible)~.bxf-checkbox__checkbox.sc-bxf-select{outline:0}.bxf-checkbox__container.sc-bxf-select input[disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.bxf-checkbox--readonly.sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-disabled=disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.bxf-checkbox--readonly.sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-disabled=disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid #6b6a67}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-disabled=disabled][data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-disabled=disabled][data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{background-color:#6b6a67;border:#6b6a67}.bxf-checkbox__container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-checkbox__checkbox.bxf-checkbox__validation-error.sc-bxf-select{border-color:#c00b28}.bxf-checkbox__container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-checkbox__checkbox.bxf-checkbox__validation-warning.sc-bxf-select{border-color:#ff8200}.bxf-checkbox__container.bxf-checkbox--disabled.sc-bxf-select,.bxf-checkbox__container.bxf-checkbox--readonly.sc-bxf-select{color:#6b6a67;pointer-events:none}.bxf-checkbox__container.bxf-checkbox--disabled.sc-bxf-select svg.sc-bxf-select:hover,.bxf-checkbox__container.bxf-checkbox--readonly.sc-bxf-select svg.sc-bxf-select:hover{cursor:default}.bxf-checkbox__container.bxf-checkbox--readonly.sc-bxf-select{color:#000000}.bxf-checkbox__label-container.sc-bxf-select{display:flex;flex-direction:row}.bxf-checkbox__label-container.sc-bxf-select:hover{cursor:pointer}.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid #000000}.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{background-color:#ffffff;border:0.0625rem solid #000000}.bxf-checkbox__label.sc-bxf-select{padding-left:8px}.bxf-checkbox__match.sc-bxf-select{font-weight:700}.bxf-checkbox__label-secondary-container.sc-bxf-select{display:flex;flex-direction:column}.bxf-checkbox__label-secondary-container--right.sc-bxf-select{display:flex;flex-direction:row;justify-content:space-between;width:100%}.bxf-checkbox__secondary-label.sc-bxf-select,.bxf-checkbox__secondary-label--right.sc-bxf-select,.bxf-checkbox-group__secondary-label.sc-bxf-select{color:#6b6a67}.bxf-checkbox__secondary-label.sc-bxf-select{padding-left:24px}.bxf-checkbox__secondary-label--right.sc-bxf-select{padding-left:12px;text-align:right}.bxf-checkbox-group__secondary-label.sc-bxf-select{padding-left:8px}@media screen and (forced-colors: active){.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) input[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__label-container.sc-bxf-select:hover:not(.bxf-checkbox--disabled):not(.bxf-checkbox__container.bxf-checkbox--readonly) .bxf-select__pseudo-checkbox[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid Highlight;background-color:Highlight}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid Highlight;background-color:Highlight}.bxf-checkbox__container.sc-bxf-select input[disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.bxf-checkbox--readonly.sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-disabled=disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.bxf-checkbox--readonly.sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-disabled=disabled].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid GrayText}.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:checked.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input.sc-bxf-select:indeterminate.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select input[data-disabled=disabled][data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate[disabled]~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:checked.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox.sc-bxf-select:indeterminate.bxf-checkbox--readonly~.bxf-checkbox__checkbox.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-select__pseudo-checkbox[data-disabled=disabled][data-default-checked=true].sc-bxf-select~.bxf-checkbox__checkbox.sc-bxf-select{border:0.0625rem solid GrayText;background-color:GrayText}.bxf-checkbox__container.sc-bxf-select .bxf-checkbox--disabled.sc-bxf-select .bxf-icon__container.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox__container.bxf-checkbox--readonly.sc-bxf-select .bxf-icon__container.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox--disabled.sc-bxf-select .bxf-icon__container.sc-bxf-select:hover,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox__checkbox.sc-bxf-select .bxf-icon__container.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox__checkbox.sc-bxf-select .bxf-icon__container.sc-bxf-select:hover,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox__regular.sc-bxf-select .bxf-icon__container.sc-bxf-select,.bxf-checkbox__container.sc-bxf-select .bxf-checkbox__regular.sc-bxf-select .bxf-icon__container.sc-bxf-select:hover{color:Canvas}}.bxf-select__container.sc-bxf-select{display:flex;flex-direction:column;min-width:3.75rem}.bxf-select__checkbox-mixed-state-2--checkbox.sc-bxf-select{color:#000000 !important}.bxf-select__checkbox-mixed-state-2--checkbox.sc-bxf-select svg.sc-bxf-select rect.sc-bxf-select{fill:currentColor;stroke:currentColor}.bxf-select__checkbox-mixed-state-2--checkbox.sc-bxf-select:hover,.bxf-select__checkbox-mixed-state-2--checkbox.sc-bxf-select:active{color:#000000}.bxf-select__checkbox-mixed-state-2--checkbox.disabled.sc-bxf-select{color:#000000 !important}.bxf-select__checkbox-mixed-state-2--checkbox.disabled.sc-bxf-select svg.sc-bxf-select rect.sc-bxf-select{fill:currentColor;stroke:currentColor}.bxf-select__checkbox-mixed-state-2--checkbox.disabled.sc-bxf-select:hover,.bxf-select__checkbox-mixed-state-2--checkbox.disabled.sc-bxf-select:active{color:#000000}.bxf-select__checkmark--checkbox.sc-bxf-select{color:#000000 !important}.bxf-select__checkmark--checkbox.sc-bxf-select:hover,.bxf-select__checkmark--checkbox.sc-bxf-select:active{color:#000000}.bxf-select__checkmark--checkbox.sc-bxf-select svg.sc-bxf-select polyline.sc-bxf-select{fill:none;stroke:currentColor}.bxf-select__checkmark--checkbox.disabled.sc-bxf-select{color:#000000 !important}.bxf-select__checkmark--checkbox.disabled.sc-bxf-select:hover,.bxf-select__checkmark--checkbox.disabled.sc-bxf-select:active{color:#000000}.bxf-select__checkmark--checkbox.disabled.sc-bxf-select svg.sc-bxf-select polyline.sc-bxf-select{fill:none;stroke:currentColor}.bxf-select__container--time-scale.sc-bxf-select{display:flex;justify-content:space-between}.bxf-select__container-left-label.sc-bxf-select{flex-direction:row}.bxf-select__container-left-label.sc-bxf-select .bxf-select__label.sc-bxf-select{padding-bottom:0;white-space:nowrap}.bxf-select__container-left-label.sc-bxf-select .bxf-select__label-container.sc-bxf-select{padding-right:12px;align-items:flex-end}.bxf-select__container-left-label.sc-bxf-select .bxf-select__label-container--compact.sc-bxf-select{padding-right:8px}.bxf-select__container-left-label.sc-bxf-select .bxf-select__label-container--time-scale.sc-bxf-select{padding-right:12px}.bxf-select__container-left-label.sc-bxf-select .bxf-select__label-container--time-scale-stacked.sc-bxf-select{align-self:flex-start;padding-top:0.4375rem}.bxf-select__input-container.sc-bxf-select,.bxf-select__input-container-large.sc-bxf-select,.bxf-select__input-container-small.sc-bxf-select{display:flex;flex-direction:row;justify-content:space-between;align-items:center;width:100%;height:32px;border:1px solid #aeaba7;border-radius:0.125rem;background-color:#ffffff;box-sizing:border-box;padding:0}.bxf-select__input-container.sc-bxf-select:focus,.bxf-select__input-container-large.sc-bxf-select:focus,.bxf-select__input-container-small.sc-bxf-select:focus{outline:0.0625rem dotted #265cb2;outline:auto #265cb2}.bxf-select__input-container.bxf__focus--keyboard-only.sc-bxf-select:focus:not(:focus-visible),.bxf__focus--keyboard-only.bxf-select__input-container-large.sc-bxf-select:focus:not(:focus-visible),.bxf__focus--keyboard-only.bxf-select__input-container-small.sc-bxf-select:focus:not(:focus-visible){outline:0}.bxf-select__input-container.sc-bxf-select:hover,.bxf-select__input-container-large.sc-bxf-select:hover,.bxf-select__input-container-small.sc-bxf-select:hover{cursor:pointer}.bxf-select__input-container-small.sc-bxf-select{height:24px}.bxf-select__input-container-small.sc-bxf-select~.bxf-select__info-container.sc-bxf-select,.bxf-select__input-container-small.sc-bxf-select .bxf-select__info-container.sc-bxf-select{margin-top:-1.5rem;height:24px}.bxf-select__input-container-small.sc-bxf-select .bxf-clear-icon__container.sc-bxf-select input.sc-bxf-select{padding-left:0.4375rem;padding-right:0.4375rem}.bxf-select__input-container-large.sc-bxf-select{height:40px}.bxf-select__input-container-large.sc-bxf-select~.bxf-select__info-container.sc-bxf-select,.bxf-select__input-container-large.sc-bxf-select .bxf-select__info-container.sc-bxf-select{margin-top:-2.5rem;height:40px}.bxf-select__input-container-large.sc-bxf-select .bxf-clear-icon__container.sc-bxf-select input.sc-bxf-select{padding-left:12px;padding-right:12px}.bxf-select__input-container--error.sc-bxf-select{background-color:#c00b281A;border-color:#c00b28}.bxf-select__input-container--error.bxf-select__input-container--focus.sc-bxf-select{background-color:#c00b281A;border-color:#c00b28;caret-color:initial}.bxf-select__input-container--warning.sc-bxf-select{background-color:#ff82001A;border-color:#ff8200}.bxf-select__input-container--warning.bxf-select__input-container--focus.sc-bxf-select{background-color:#ff82001A;border-color:#ff8200;caret-color:initial}.bxf-select__input-container--disabled.sc-bxf-select,.bxf-select__input-container--readonly.sc-bxf-select{background-color:#eae7e1;border-color:#eae7e1;-moz-user-focus:none;-webkit-user-focus:none;-ms-user-focus:none;-moz-user-modify:read-only;-webkit-user-modify:read-only;-ms-user-modify:read-only;-moz-user-select:none;-webkit-user-select:none;-ms-user-select:none;user-select:none}.bxf-select__input-container--disabled.sc-bxf-select:hover,.bxf-select__input-container--readonly.sc-bxf-select:hover{cursor:default}.bxf-select__input-container--disabled.sc-bxf-select input.sc-bxf-select::placeholder,.bxf-select__input-container--readonly.sc-bxf-select input.sc-bxf-select::placeholder{color:#aeaba7}.bxf-select__input-container--disabled.sc-bxf-select input.sc-bxf-select:hover,.bxf-select__input-container--readonly.sc-bxf-select input.sc-bxf-select:hover{cursor:default}.bxf-select__input-container--disabled.sc-bxf-select .bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select,.bxf-select__input-container--readonly.sc-bxf-select .bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select{color:#aeaba7 !important}.bxf-select__input-container--disabled.sc-bxf-select .bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select svg.sc-bxf-select polygon.sc-bxf-select,.bxf-select__input-container--readonly.sc-bxf-select .bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select svg.sc-bxf-select polygon.sc-bxf-select{fill:currentColor !important}.bxf-select__input-container--disabled.sc-bxf-select svg.sc-bxf-select:hover,.bxf-select__input-container--readonly.sc-bxf-select svg.sc-bxf-select:hover{cursor:default}.bxf-select__input-container--readonly.sc-bxf-select{pointer-events:none}.bxf-select__input-container--readonly.sc-bxf-select input.sc-bxf-select::placeholder{color:#000000}.bxf-select__input.sc-bxf-select:focus:hover{cursor:pointer}.bxf-select__input.sc-bxf-select:focus::placeholder{color:transparent}.bxf-select__input-container--focus.bxf-select__input-container--key-focus.sc-bxf-select{outline:0.0625rem dotted var(--primary-action__color);outline:auto var(--primary-action__color)}.bxf-select__input-container--focus.bxf-select__input-container--key-focus.bxf-select__input-container--clear-focus.sc-bxf-select{outline:none}.bxf-select__input-container--focus.sc-bxf-select .bxf-select__input.sc-bxf-select:hover{cursor:pointer}.bxf-select__input-container--focus.sc-bxf-select .bxf-select__input.sc-bxf-select::placeholder{color:transparent}.bxf-select__input-container--focus.bxf__mouse-focus.sc-bxf-select{outline:none;border:1px solid #ff4713;border-radius:2px 2px 0 0;background-color:#ff47130F;caret-color:#ff4713}.bxf-select__input-container--focus.bxf-select__input-container--readonly.sc-bxf-select input.sc-bxf-select::placeholder{color:#000000}.bxf-select__input.sc-bxf-select{width:100%;height:100%;min-width:0.8125rem;color:#000000;line-height:1.2307692308;border:none;outline:none;background-color:transparent;padding:0;padding-left:12px;text-overflow:ellipsis;white-space:nowrap;display:inline-block;text-align:start}.bxf-select__input.sc-bxf-select:hover{cursor:pointer}.bxf-select__input.sc-bxf-select::placeholder{color:#000000;opacity:1}.bxf-select__input-small.sc-bxf-select{line-height:1.25;padding-left:8px;padding-right:8px}.bxf-select__input-large.sc-bxf-select{line-height:1.0714285714;padding-left:12px;padding-right:12px}.bxf-select__input--combobox.sc-bxf-select{padding-left:0;padding-right:0}.bxf-select__input--disabled.sc-bxf-select{color:#eae7e1}.bxf-select__input-simple-select--disabled.sc-bxf-select{pointer-events:none}.bxf-select__icon.sc-bxf-select{display:flex;flex-direction:row;justify-content:center;align-content:center;align-items:center;height:100%;margin-right:-0.125rem;margin-left:8px;padding-right:12px;min-width:16px}.bxf-select__icon.sc-bxf-select svg.sc-bxf-select{transform:scale(1.3458950202, 1.4354066986);-webkit-transform:scale(1.3458950202, 1.4354066986)}.bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select{color:#000000 !important}.bxf-select__icon.sc-bxf-select .bxf-icon__container.sc-bxf-select svg.sc-bxf-select polygon.sc-bxf-select{fill:currentColor !important}.bxf-select__icon-small.sc-bxf-select{margin-left:4px;padding-right:8px}.bxf-select__icon-large.sc-bxf-select{margin-left:12px;padding-right:16px}.bxf-select__icon--focus.sc-bxf-select .bxf-icon__container.sc-bxf-select{color:#ff4713 !important}.bxf-select__icon--focus.sc-bxf-select .bxf-icon__container.sc-bxf-select svg.sc-bxf-select polygon.sc-bxf-select{fill:currentColor !important}.bxf-select__label-container.sc-bxf-select{align-items:center;display:inline-flex}.bxf-select__label-container--left.sc-bxf-select{padding-bottom:0.5625rem}.bxf-select__label-container--left-small.sc-bxf-select{padding-bottom:4px}.bxf-select__label-container--left-large.sc-bxf-select{padding-bottom:12px}.bxf-select__label-container--left-validation.sc-bxf-select{padding-bottom:1.75rem}.bxf-select__label-container--left-validation-small.sc-bxf-select{padding-bottom:1.5625rem}.bxf-select__label-container--left-validation-large.sc-bxf-select{padding-bottom:1.9375rem}.bxf-select__input-wrapper.sc-bxf-select{width:100%}.bxf-select__input-wrapper--time-scale.sc-bxf-select{width:4.6875rem;align-self:flex-end}.bxf-select__label.sc-bxf-select{display:inline-block;max-width:fit-content;min-width:fit-content;width:fit-content;color:#000000;line-height:1.2307692308;padding-bottom:8px}.bxf-select__label--compact.sc-bxf-select{padding-bottom:4px}.bxf-select__label--instructional-text.sc-bxf-select{padding-bottom:4px}.bxf-select__label-asterisk.sc-bxf-select{padding-left:4px;color:#c00b28;line-height:0.9375;float:right;padding-bottom:4px}.bxf-select__label--disabled.sc-bxf-select{color:#aeaba7}.bxf-select__info-container.sc-bxf-select{margin-top:-2rem;height:32px;display:flex;flex-direction:row;justify-content:flex-start;align-items:center;margin-left:4px;width:fit-content}.bxf-select__info-container.sc-bxf-select+input.sc-bxf-select{display:none}.bxf-select__info-container.sc-bxf-select+.bxf-select__separator-container.sc-bxf-select{margin-left:8px}.bxf-select__separator-container.sc-bxf-select{display:flex;align-items:center}.bxf-select__separator-container.sc-bxf-select+.bxf-select__info-container.sc-bxf-select{margin-top:0;margin-left:8px}.bxf-select__separator.sc-bxf-select{width:1px;height:1.25rem;background-color:#d9d6ce}.bxf-select__separator-small.sc-bxf-select{height:16px}.bxf-select__separator-large.sc-bxf-select{height:24px}.bxf-select__separator--focus.sc-bxf-select{background-color:#ff4713}.bxf-select__separator--focus.bxf-select__separator--key-focus.sc-bxf-select{background-color:#ff4713}.bxf-select__separator--focus.bxf-select__separator--key-focus.bxf-select__separator--error.sc-bxf-select{background-color:#c00b281A}.bxf-select__separator--focus.bxf-select__separator--key-focus.bxf-select__separator--warning.sc-bxf-select{background-color:#ff82001A}.bxf-select__separator--hidden.sc-bxf-select{visibility:hidden}.bxf-select__separator--error.sc-bxf-select{background-color:#c00b281A}.bxf-select__separator--error.bxf-select__separator--focus.sc-bxf-select{background-color:#ff4713}.bxf-select__separator--warning.sc-bxf-select{background-color:#ff82001A}.bxf-select__separator--warning.bxf-select__separator--focus.sc-bxf-select{background-color:#ff4713}.bxf-select__message.sc-bxf-select,.bxf-select__message--warning.sc-bxf-select,.bxf-select__message--error.sc-bxf-select{display:inline-block;line-height:1.2307692308;padding-top:4px}.bxf-select__message--error.sc-bxf-select{color:#c00b28}.bxf-checkbox__label-container.sc-bxf-select{text-overflow:ellipsis;white-space:nowrap}.bxf-select__dropdown.sc-bxf-select{padding:4px 0;margin:0}.bxf-select__dropdown[data-bxf-is-loading=true].sc-bxf-select{display:none}.bxf-select__dropdown-content[data-bxf-is-loading=true].sc-bxf-select{display:none}.bxf-select__dropdown[data-bxf-is-windows-os=true].sc-bxf-select:hover{cursor:pointer}.bxf-select__dropdown[data-bxf-is-windows-os=true].sc-bxf-select:hover,.bxf-select__dropdown[data-bxf-is-windows-os=true].sc-bxf-select:focus-within{overflow:auto !important}.bxf-select__dropdown-container.sc-bxf-select{pointer-events:auto;display:flex;flex-direction:column;top:0;left:0;box-shadow:0 4px 12px 0 #aeaba7;color:#000000;border:1px solid #aeaba7;border-bottom:1px solid #ff4713;box-sizing:border-box;border-radius:2px 2px 0 0;background-color:#ffffff;position:absolute}.bxf-select__dropdown-listbox.sc-bxf-select{display:flex;flex-direction:column;box-sizing:border-box;max-height:100%;flex-grow:1;height:0}.bxf-select__dropdown-listbox.sc-bxf-select:focus{outline:0}.bxf-select__option.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select{line-height:2.4615384615;height:32px;white-space:nowrap;text-overflow:ellipsis}.bxf-select__option--compact.sc-bxf-select{line-height:1.5384615385;height:1.25rem}.bxf-select__option.sc-bxf-select:focus,.bxf-select__option-group-label.sc-bxf-select:focus{outline:none}.bxf-select__option.sc-bxf-select:focus.bxf-select__option--single,.bxf-select__option-group-label.sc-bxf-select:focus.bxf-select__option--single{outline:0.0625rem dotted var(--primary-action__color);outline:auto var(--primary-action__color);outline-offset:-0.125rem}.bxf-select__option.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible),.bxf-select__option-group-label.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible){outline:none}.bxf-select__option.sc-bxf-select div.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select div.sc-bxf-select{white-space:nowrap;text-overflow:ellipsis}.bxf-select__option.sc-bxf-select .bxf-checkbox__container.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select .bxf-checkbox__container.sc-bxf-select{width:100%;height:100%;padding-left:12px;padding-right:12px;overflow:visible}.bxf-select__option.sc-bxf-select .bxf-checkbox__container.sc-bxf-select .bxf-checkbox__label.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select .bxf-checkbox__container.sc-bxf-select .bxf-checkbox__label.sc-bxf-select{overflow:hidden;text-overflow:ellipsis}.bxf-select__option-span.sc-bxf-select,.bxf-select__option-group-label.sc-bxf-select{display:block;text-overflow:ellipsis;overflow:hidden;margin-left:12px;margin-right:12px}.bxf-select__option-container.sc-bxf-select,.bxf-select__option-container-default.sc-bxf-select{list-style:none;width:100%;cursor:default}.bxf-select__option-container.bxf-select__option-container-disabled.sc-bxf-select,.bxf-select__option-container-disabled.bxf-select__option-container-default.sc-bxf-select{color:#aeaba7;pointer-events:none}.bxf-select__option-container[data-bxf-interactive=true].sc-bxf-select:hover,[data-bxf-interactive=true].bxf-select__option-container-default.sc-bxf-select:hover{cursor:pointer}.bxf-select__option-container[data-bxf-interactive=true].sc-bxf-select:hover,[data-bxf-interactive=true].bxf-select__option-container-default.sc-bxf-select:hover{background-color:#ff47131A}.bxf-select__option-border.sc-bxf-select{background-color:#d9d6ce;height:0.0625rem;margin-top:4px;margin-bottom:4px}.bxf-select__option-border.sc-bxf-select:hover{background-color:#ff4713}.bxf-select__option-checkbox.sc-bxf-select{display:flex;flex-direction:row;align-items:center}.bxf-select__option-checkbox.sc-bxf-select:focus{outline:0}.bxf-select__option-checkbox.sc-bxf-select:focus .bxf-checkbox__checkbox.sc-bxf-select{outline:0.0625rem dotted var(--primary-action__color);outline:auto var(--primary-action__color)}.bxf-select__option-checkbox.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible) .bxf-checkbox__checkbox.sc-bxf-select{outline:0}.bxf-select__option-checkbox.sc-bxf-select bxf-checkbox.sc-bxf-select{display:flex;width:100%;height:100%;white-space:nowrap;text-overflow:ellipsis}.bxf-select__option-checkbox-large.sc-bxf-select .bxf-checkbox__container.sc-bxf-select{padding-left:16px;padding-right:16px}.bxf-select__option--selected.sc-bxf-select{background-color:#ff47131A}.bxf-select__option-container-default.sc-bxf-select{outline-offset:-0.125rem;border-bottom:1px solid #d9d6ce;box-sizing:border-box;padding:4px 0;height:2.625rem;color:#000000;background-color:#ffffff}.bxf-select__option-container-default.sc-bxf-select:focus{outline:0.0625rem dotted #265cb2;outline:auto #265cb2}.bxf-select__option-container-default.bxf__focus--keyboard-only.sc-bxf-select:focus:not(:focus-visible){outline:0}.bxf-select__option-container-default.sc-bxf-select:hover{background-color:#ff47131A}.bxf-select__option-container-default--compact.sc-bxf-select{height:1.875rem}.bxf-select__option-container-default.sc-bxf-select .bxf-select__option.sc-bxf-select:hover,.bxf-select__option-container-default.sc-bxf-select .bxf-select__option-group-label.sc-bxf-select:hover{cursor:pointer}.bxf-select__option-container-default.sc-bxf-select .bxf-select__option.sc-bxf-select:hover,.bxf-select__option-container-default.sc-bxf-select .bxf-select__option-group-label.sc-bxf-select:hover{background-color:transparent}.bxf-select__option-container-default--checkbox.sc-bxf-select:focus{outline:0}.bxf-select__option-container-default--checkbox.sc-bxf-select:focus .bxf-checkbox__checkbox.sc-bxf-select{outline:0.0625rem dotted var(--primary-action__color);outline:auto var(--primary-action__color)}.bxf-select__option-container-default--checkbox.sc-bxf-select:focus.bxf__focus--keyboard-only:not(:focus-visible) .bxf-checkbox__checkbox.sc-bxf-select{outline:0}.bxf-select__option-container-default--initial.sc-bxf-select{margin:0;border-top:none;border-left:none;border-right:none;text-align:left}.bxf-select__action-button-container.sc-bxf-select{display:flex;flex-direction:row;justify-content:flex-end;border-top:1px solid #aeaba7;padding:8px}.bxf-select__loading.sc-bxf-select{background-color:#ff47130F;text-align:center;margin:8px;padding:12px;line-height:1.5384615385}.bxf-select__option-group-label.sc-bxf-select{color:#6b6a67}.bxf-clear-icon__container[data-bxf-clear-icon-visible=true].sc-bxf-select input[data-bxf-size=large].sc-bxf-select{width:calc(100% - 4rem)}.bxf-clear-icon__container[data-bxf-clear-icon-visible=true].sc-bxf-select input[data-bxf-size=large].sc-bxf-select~.bxf-clear-icon.sc-bxf-select{padding:0 16px}.bxf-select__table-footer.sc-bxf-select{margin-top:4px;padding:12px}.bxf-select__table-cell.sc-bxf-select,.bxf-select__table-header-cell.sc-bxf-select{padding-left:12px;padding-right:12px;border-bottom:0.0625rem solid #eae7e1;line-height:1.2307692308;height:32px;color:#000000;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.bxf-select__table-cell[data-overflow=false].sc-bxf-select,[data-overflow=false].bxf-select__table-header-cell.sc-bxf-select{max-width:0}.bxf-select__table-header-cell.sc-bxf-select{overflow:initial;max-width:100%;color:#6b6a67}.bxf-select__table.sc-bxf-select{width:100%;border-collapse:collapse}.bxf-select__table-row.sc-bxf-select{outline-offset:-0.125rem}.bxf-select__table-row.sc-bxf-select:focus{outline:0.0625rem dotted var(--primary-action__color);outline:auto var(--primary-action__color)}.bxf-select__table-row.bxf__focus--keyboard-only.sc-bxf-select:focus:not(:focus-visible){outline:0}.bxf-select__table-row.sc-bxf-select:hover{background-color:#ff471312}.bxf-select__dropdown-content.sc-bxf-select{width:100%;overflow:auto;list-style:none;margin:0;max-height:15.625rem;padding:4px 0;height:100%}.bxf-select__typeahead-container.sc-bxf-select{box-sizing:border-box;border-radius:0.125rem;background-color:#ffffff;box-shadow:0 4px 12px 0 #d9d6ce;cursor:default;color:#000000;position:absolute;width:auto !important;height:auto !important}.bxf-select__instructional-text.sc-bxf-select{color:#aeaba7;padding-bottom:8px}@media (forced-colors: active){.bxf-select__input.sc-bxf-select::placeholder{color:ButtonText}.bxf-select__input.bxf-select__input--combobox.sc-bxf-select::placeholder{color:GrayText}}";
const BxfSelectStyle0 = bxfSelectCss;

const BxfSelect = /*@__PURE__*/ proxyCustomElement(class BxfSelect extends H {
    constructor() {
        super();
        this.__registerHost();
        this.advancedSelectionChanged = createEvent(this, "advancedSelectionChanged", 7);
        this.selectionChanged = createEvent(this, "selectionChanged", 7);
        this.typeaheadValueChanged = createEvent(this, "typeaheadValueChanged", 7);
        this.dropdownOpened = createEvent(this, "dropdownOpened", 7);
        this.dropdownClosed = createEvent(this, "dropdownClosed", 7);
        this.clearButtonClicked = createEvent(this, "clearButtonClicked", 7);
        this.formValueChanged = createEvent(this, "formValueChanged", 7);
        this.applyButtonClicked = createEvent(this, "applyButtonClicked", 7);
        this.namespaceI18n = undefined;
        this.i18nKeys = undefined;
        this.maxDropdownHeight = 234;
        this.maxDropdownWidth = undefined;
        this.isLoading = false;
        this.size = 'regular';
        this.isOpen = false;
        this.isCompact = BxfDefaultState.isCompact;
        this.inputValueRaw = '';
        this.validator = undefined;
        this.overridePlacement = undefined;
        this.isRequired = false;
        this.errorMessage = null;
        this.validationType = BxfFormValidationType.ERROR;
        this.isValid = true;
        this.data = [];
        this.type = 'simple';
        this.isDisabled = false;
        this.isReadOnly = false;
        this.hasInitialOptionPlaceholder = true;
        this.label = undefined;
        this.isLeftLabel = false;
        this.instructionalText = undefined;
        this.isTimeScale = false;
        this.isStacked = false;
        this.shouldCloseOverlays = true;
        this.selected = undefined;
        this.applyButton = false;
        this.highlightMode = 'popular';
        this.isTracking = true;
        this.isClone = false;
        this.trackingData = undefined;
        this.keepMouseFocus = false;
        this.assistiveText = '';
        this.parentOverlayContainerSelector = 'body';
        this.dataTestSuffix = undefined;
        this.hasTooltip = true;
        this.displayPillTooltip = false;
        this.displayAllSelected = false;
        this.debounceTime = undefined;
        this.clearButtonLabel = 'Clear';
        this.deleteLabel = 'Delete';
        this.lockedLabel = 'Locked';
        this.select = BxfSelectInitialOption.SELECT;
        this.applyButtonIsDisabled = true;
        this.componentType = undefined;
        this.initialOptionValue = BxfSelectInitialOption.SELECT;
        this.inputValue = this.select;
        this.initialOptionState = 'initial';
        this.optionsCount = 0;
        this.all = BxfSelectInitialOption.ALL;
        this.allSelected = BxfSelectInitialOption.ALL_SELECTED;
        this.selectAll = BxfSelectInitialOption.SELECT_ALL;
        this.selectNone = BxfSelectInitialOption.SELECT_NONE;
        this.applyButtonLabel = 'Apply';
        this.selectedLabel = 'selected';
        this.loadingLabel = 'Loading';
    }
    _validator = defaultValidator;
    previousSelected;
    i18n;
    _focusSource;
    shouldWatchSelected = true;
    _componentDidLoad = false;
    clearDelegate;
    /**
     * Whether or not the input has focus
     */
    inputFocus = false;
    /**
     * Whether or not input has keyboard focus
     */
    inputKeyFocus = false;
    delayedSearch;
    shouldEmitInputValueChanged = true;
    get host() { return this; }
    // #endregion "Props"
    // #region "Events"
    /**
     * This is an event that is emitted when the selection changes.
     * The detail contains the rich information about value/s selected.
     */
    advancedSelectionChanged;
    /**
     * This is an event that is emitted when the selection changes.
     * The detail contains the value/s selected.
     */
    selectionChanged;
    /**
     * An event that is triggered on every value change emitted by the input on key press.
     * The event is affected by the debounce time.
     */
    typeaheadValueChanged;
    /**
     * This event is emitted when popover overlay is opened
     */
    dropdownOpened;
    /**
     * This event is emitted when popover overlay is closed
     */
    dropdownClosed;
    /**
     * This event is emitted on clear icon click
     */
    clearButtonClicked;
    /**
     * An event that is triggered when using this component in forms.
     */
    formValueChanged;
    /**
     * An event that is triggered when pressing on the apply button.
     */
    applyButtonClicked;
    // #endregion "State"
    isInputValueRawChanged() {
        setTimeout(() => {
            this.componentType.isInputValueRawChangeHandler?.();
        }, 10);
    }
    isLoadingChanged() {
        setTimeout(() => {
            if (this.isOpen && this.componentType.isLoadingChangedHandler) {
                this.componentType.isLoadingChangedHandler();
                this.focusInput();
            }
        }, 10);
    }
    isOpenChanged() {
        if (this.isOpen) {
            this.componentType.openDropdown();
        }
        else {
            this.componentType.closeDropdown();
        }
    }
    selectedDidChange(newValue, oldValue) {
        if (isEmpty(oldValue)) {
            this.componentType?.setValue(newValue);
        }
        else if (this.shouldWatchSelected && !isEqual(newValue, oldValue)) {
            this.componentType?.setValue(this.selected);
        }
        this.shouldWatchSelected = true;
    }
    keepMouseFocusHandler(newValue) {
        const el = this.host.querySelector(`.${BxfSelectConstants.STRING.INPUT_CONTAINER}`);
        toggleMouseFocus(el, newValue);
    }
    typeChangedHandler() {
        this.renderType();
    }
    dataChangedHandler() {
        if (this.data == null) {
            this.data = [];
            this.componentType?.setValue(null);
            return;
        }
        this.initDataInternalIds();
        this.updateOptionCount();
        if (this.componentType && this.componentType.dataDidUpdate) {
            this.componentType.dataDidUpdate();
        }
    }
    isOpenChangedHandler() {
        if (this.componentType.isDropdownOpenedDidChange) {
            this.componentType.isDropdownOpenedDidChange();
        }
        if (this.isOpen) {
            this.dropdownOpened.emit({ srcEvent: null });
        }
        else {
            this.dropdownClosed.emit({ srcEvent: null });
        }
    }
    // #region "Validator"
    /**
     * Public method to validate selected options with current validator
     * @return {Promise<void>}
     */
    async validate() {
        if (this.validator) {
            this._validator = getValidator(this.validator);
            this.isValid = this._validator.validate(this.componentType.selectedOptions);
            this.errorMessage = this._validator.errorMessage
                ? this._validator.errorMessage
                : this.errorMessage;
        }
    }
    /**
     * Used to toggle disable for forms
     * @returns {Promise<void>}
     * @param action controls disable state for the select
     */
    async toggleDisable(action) {
        this.isDisabled = action;
    }
    /**
     * Sets the value of the component by passing a string or an BxfSelectOption for one selection,
     * or an Array<BxfSelectOption> for multiple selections.
     * @param newValue{BxfSelectFormValue}
     */
    async setValue(newValue) {
        if (isEmpty(newValue)) {
            // This will reset the data to an empty array
            // and set selected to empty value
            this.componentType?.setValue(null);
        }
        else {
            const data = get(newValue, 'data');
            if (data !== undefined && !isEqual(data, this.data)) {
                this.data = data || [];
            }
            const _selectedValue = get(newValue, 'selectedValue');
            if (_selectedValue !== undefined &&
                !isEqual(this.selected, _selectedValue)) {
                this.componentType?.setValue(_selectedValue);
            }
        }
    }
    /**
     * Returns whether or not text input has value used for required validation in forms
     * @returns {Promise<boolean>}
     */
    async validateForRequired(newValue) {
        return new Promise((resolve) => {
            // should return true if value is not provided and required validator should return error
            const values = map(head(map(get(newValue, 'data'), 'values')), 'isSelected');
            const hasSelectedValue = includes(values, true);
            resolve(isEmpty(get(newValue, 'selectedValue')) && !hasSelectedValue);
        });
    }
    // #endregion "Validator"
    /**
     * Public method that would open the dropdown overlay
     * @returns {Promise<void>}
     */
    async open() {
        this.isOpen = true;
    }
    /**
     * Public method that would close the dropdown overlay
     * @returns {Promise<void>}
     */
    async close() {
        this.isOpen = false;
    }
    /**
     * This method allows for focusing on the current input
     */
    async focusInput() {
        this.componentType?.focusInput?.();
    }
    // #region "Lifecycle"
    componentWillLoad() {
        if (isUsingI18n() && this.namespaceI18n) {
            this.initI18n();
        }
        if (this.isClone) {
            // Create a new reference of the data and the objects inside
            // to differentiate between other selects that are using the same reference
            this.data = cloneDeep(this.data);
        }
        this.initDataInternalIds();
        this.renderType();
        this.initDelayedSearch();
        this.initValidators();
    }
    componentDidRender() {
        if (this.componentType.componentDidRender) {
            this.componentType.componentDidRender();
        }
    }
    componentWillUpdate() {
        this.renderType();
        this.initDelayedSearch();
    }
    componentDidUpdate() {
        if (get(this, 'host.parentNode')) {
            this._componentDidLoad = true;
        }
    }
    componentDidLoad() {
        if (this.componentType.componentDidLoad) {
            this.componentType.componentDidLoad();
        }
        if (this.selected) {
            this.selectedDidChange(this.selected, null);
        }
        this.updateOptionCount();
        this._componentDidLoad = true;
    }
    disconnectedCallback() {
        this._componentDidLoad = false;
        if (this.componentType && this.componentType.componentDisconnected) {
            this.componentType.componentDisconnected();
        }
        this.i18n?.destroyTranslations();
    }
    // #endregion "Lifecycle"
    initI18n() {
        this.i18n = new BxfI18n(this.namespaceI18n);
        window['i18n'].on('languageChanged', () => {
            this.i18n.languageChangedHandler(this.translateText());
        });
        window['i18n'].on('loaded', () => {
            this.translateText();
        });
    }
    translateText() {
        translateKeys(this, this.i18nKeys, this.i18n);
        this.data.forEach((selectOptionGroup) => {
            translateKeys(selectOptionGroup, selectOptionGroup.i18nKeys, this.i18n);
            selectOptionGroup.values?.forEach((selectOption) => {
                translateKeys(selectOption, selectOption.i18nKeys, this.i18n);
            });
        });
        this.initialOptionValue = this.select;
        this.inputValue = this.select;
    }
    /**
     * Get appropriate label for listbox for screen readers
     */
    get baseLabel() {
        return this.label ? this.label : this.assistiveText;
    }
    delayedSearchHandler(inputValueRaw) {
        this.inputValueRaw = inputValueRaw;
        this.validate();
        if (this.shouldEmitInputValueChanged) {
            this.typeaheadValueChanged.emit({
                srcEvent: null,
                value: { displayValue: inputValueRaw },
            });
        }
        else {
            this.shouldEmitInputValueChanged = true;
        }
    }
    /**
     * Private function to update option count.
     * @returns {void}
     */
    updateOptionCount() {
        this.optionsCount = 0;
        this.data.forEach((optionGroup) => {
            this.optionsCount += optionGroup.values.length;
        });
    }
    /**
     * Private function to initialize validators.
     */
    initValidators() {
        if (this.validator) {
            this._validator = getValidator(this.validator);
            if (!isEmpty(this.componentType.selectedOptions)) {
                this.validate();
            }
        }
    }
    initDelayedSearch() {
        if (!this.delayedSearch) {
            this.delayedSearchHandler = this.delayedSearchHandler.bind(this);
            if (this.debounceTime) {
                this.delayedSearch = debounce(this.delayedSearchHandler, this.debounceTime);
            }
            else {
                this.delayedSearch = this.delayedSearchHandler;
            }
            this.delayedSearch = this.delayedSearch.bind(this);
        }
    }
    advancedSelectionChangedHandler() {
        if (get(this, 'previousSelected', []) instanceof Array &&
            get(this, 'selected', []) instanceof Array) {
            const added = difference(this.selected, this.previousSelected);
            const removed = difference(this.previousSelected, this.selected);
            return {
                eventData: this.selected,
                change: {
                    added,
                    removed,
                },
                type: isEmpty(added) ? 'removed' : 'added',
                srcEvent: null,
            };
        }
        return {
            eventData: this.selected,
            type: 'changed',
            srcEvent: null,
        };
    }
    setSelected(newSelected, emit = true, event = null, optionReset = false) {
        if (!isEqual(newSelected, this.selected)) {
            this.shouldWatchSelected = false;
            this.previousSelected = this.selected;
            this.selected = newSelected;
        }
        if (emit && this._componentDidLoad && (event || optionReset)) {
            this.selectionChanged.emit({ srcEvent: event, value: newSelected });
            this.advancedSelectionChanged.emit({
                srcEvent: event,
                ...this.advancedSelectionChangedHandler(),
            });
            this.formValueChanged.emit({
                value: { selectedValue: this.selected, data: this.data },
            });
        }
    }
    initDataInternalIds() {
        if (!isEmpty(this.data)) {
            this.data.forEach((optionGroup) => {
                if (isEmpty(optionGroup.bxfId)) {
                    optionGroup.bxfId = uuidv4();
                }
                if (!isEmpty(optionGroup.values)) {
                    optionGroup.values.forEach((option) => {
                        if (isEmpty(option.bxfId)) {
                            option.bxfId = uuidv4();
                        }
                    });
                }
            });
        }
    }
    renderType() {
        if (!this.componentType) {
            switch (this.type) {
                case 'simple':
                    this.componentType = new BxfSelectSimple(this);
                    break;
                case 'multiple':
                    this.componentType = new BxfSelectMultiple(this);
                    break;
                case 'filter-simple':
                    this.componentType = new BxfSelectFilterSimple(this);
                    break;
                case 'filter-multiple':
                    this.componentType = new BxfSelectFilterMultiple(this);
                    break;
                case 'tabular':
                    this.componentType = new BxfSelectTabular(this);
                    break;
                default:
                    throw new Error(`The attribute type="${this.type}" is incorrect or incompatible with this component.`);
            }
        }
    }
    render() {
        return this.componentType.render(createSelector(this.host.tagName, this.dataTestSuffix));
    }
    static get watchers() { return {
        "inputValueRaw": ["isInputValueRawChanged"],
        "isLoading": ["isLoadingChanged"],
        "isOpen": ["isOpenChanged", "isOpenChangedHandler"],
        "selected": ["selectedDidChange"],
        "keepMouseFocus": ["keepMouseFocusHandler"],
        "type": ["typeChangedHandler"],
        "data": ["dataChangedHandler"]
    }; }
    static get style() { return BxfSelectStyle0; }
}, [2, "bxf-select", {
        "namespaceI18n": [1, "namespace-i-1-8n"],
        "i18nKeys": [16],
        "maxDropdownHeight": [2, "max-dropdown-height"],
        "maxDropdownWidth": [2, "max-dropdown-width"],
        "isLoading": [1540, "is-loading"],
        "size": [1],
        "isOpen": [1540, "is-open"],
        "isCompact": [4, "is-compact"],
        "inputValueRaw": [1537, "input-value-raw"],
        "validator": [16],
        "overridePlacement": [1, "override-placement"],
        "isRequired": [4, "is-required"],
        "errorMessage": [1537, "error-message"],
        "validationType": [1, "validation-type"],
        "isValid": [1540, "is-valid"],
        "data": [1040],
        "type": [513],
        "isDisabled": [1540, "is-disabled"],
        "isReadOnly": [516, "is-read-only"],
        "hasInitialOptionPlaceholder": [1028, "has-initial-option-placeholder"],
        "label": [513],
        "isLeftLabel": [4, "is-left-label"],
        "instructionalText": [513, "instructional-text"],
        "isTimeScale": [516, "is-time-scale"],
        "isStacked": [516, "is-stacked"],
        "shouldCloseOverlays": [4, "should-close-overlays"],
        "selected": [1025],
        "applyButton": [4, "apply-button"],
        "highlightMode": [1, "highlight-mode"],
        "isTracking": [4, "is-tracking"],
        "isClone": [4, "is-clone"],
        "trackingData": [16],
        "keepMouseFocus": [516, "keep-mouse-focus"],
        "assistiveText": [1, "assistive-text"],
        "parentOverlayContainerSelector": [513, "parent-overlay-container-selector"],
        "dataTestSuffix": [8, "data-test-suffix"],
        "hasTooltip": [4, "has-tooltip"],
        "displayPillTooltip": [4, "display-pill-tooltip"],
        "displayAllSelected": [4, "display-all-selected"],
        "debounceTime": [2, "debounce-time"],
        "clearButtonLabel": [1, "clear-button-label"],
        "deleteLabel": [1, "delete-label"],
        "lockedLabel": [1, "locked-label"],
        "select": [1],
        "selectAll": [1, "select-all"],
        "applyButtonIsDisabled": [32],
        "componentType": [32],
        "initialOptionValue": [32],
        "inputValue": [32],
        "initialOptionState": [32],
        "optionsCount": [32],
        "all": [32],
        "allSelected": [32],
        "selectNone": [32],
        "applyButtonLabel": [32],
        "selectedLabel": [32],
        "loadingLabel": [32],
        "validate": [64],
        "toggleDisable": [64],
        "setValue": [64],
        "validateForRequired": [64],
        "open": [64],
        "close": [64],
        "focusInput": [64]
    }, undefined, {
        "inputValueRaw": ["isInputValueRawChanged"],
        "isLoading": ["isLoadingChanged"],
        "isOpen": ["isOpenChanged", "isOpenChangedHandler"],
        "selected": ["selectedDidChange"],
        "keepMouseFocus": ["keepMouseFocusHandler"],
        "type": ["typeChangedHandler"],
        "data": ["dataChangedHandler"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bxf-select"];
    components.forEach(tagName => { switch (tagName) {
        case "bxf-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BxfSelect);
            }
            break;
    } });
}

export { BxfSelect as B, defineCustomElement as d };
